import { Injectable } from '@angular/core';
import { DatabaseShutdownDescription, DatabaseShutdownPhase, DatabaseWrapper, DatabaseWrapperProvider, DocChange, DocWatcher, TransactionId } from 'services/database-wrapper';
import { WriteOutcome } from 'services/database-wrapper-save-recovery';
import { buildSingletonInitPlan } from 'services/database-wrapper-sync-boot-policy';
import { bootTimings, BootPhase } from 'app/boot-timings';
import { DateTime } from 'luxon';
import { UserAuth } from 'services/auth.service';
import { clearWarmResumeFlag } from 'services/warm-resume-storage';
import { Food, FoodHeader, INFOODS, NutrientTargets, normalizeFoodFormat } from '@cf-shared/data-model/food';
import { Recipe } from '@cf-shared/data-model/recipe';
import { isoTimeToHours } from '@cf-shared/time';
import { unionBy, uniq } from 'lodash-es';
import {
  ImmutableState,
  DeclaredState,
  FitnessState,
  DebugState,
  ProfileState,
  NutritionCoachingType,
  NutritionTrackingType,
  MacroTargetsType,
  isUsersTrainingGoalGrowth,
  usesCalorieCycling,
  getWorkoutsPerWeek,
} from '@cf-shared/data-model/user-state';
// Everything this file used to import from the pure task-status helpers moved with the
// subsystem into day-task-status.service.ts (df-3b8918aefa6a), except the event payload's
// cluster type and the initial-sync wait the start-up uses for a dropped read (#6248).
import { InitialSyncWaitFor, InitialSyncWaitResult, ReactiveDayStatusCluster, waitForInitialSyncAsync } from './user-db.task-status';
import { BootSingletonReadKind, readUnreadableSingletonsFromDeviceAsync } from './boot-singleton-read';
import { reviveEntriesHoldingUserData } from './user-db.delete-boundary';
import { chooseBacklogEntriesToSave, newPastEntries } from './user-db.backlog-save';
import { PastBackfillHold } from './user-db.past-backfill-hold';
import { chooseResaveForDay, ResaveDayDecision } from './user-db.resave-day';
import { readStoredJson } from './user-db.stored-context';
import { createOncePerConditionReporter } from 'util/once-per-condition-reporter';
import { carryCommittedEditsOntoSaveTarget } from './singleton-save-target';
import { emptySchedule, generateScheduleDaysFromRules, generateScheduleLogEntries, keepOneEntryPerId, ReissuedLogEntryIdInfo, RescuedLogEntryInfo, Schedule } from '@cf-shared/data-model/schedule';
import { TelemetryData } from '@cf-shared/telemetry-shared';
import { deepClone, deepCompareEQ, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { createGenericEntry, getEntryLocalTimeInDesiredTimeZone, getEntryTimeFromID, isDayLogEntry, LogRecordState, LogRecordType, Log_Entry } from '@cf-shared/data-model/user-log';
import { pickOneEntryPerSingletonSlot } from '@cf-shared/data-model/log-entry-user-data';
import { describeDroppedSingletonEntries } from './duplicate-singleton-report';
import { describeDroppedDuplicateMeals } from './duplicate-meal-report';
import { createWorkoutEntry, Log_Workout } from '@cf-shared/data-model/log-workout';
import { ExerciseChangeDoc } from '@cf-shared/data-model/exercise-change';
import { createMealEntry, Log_Meal, pickOneMealPerSlot, renameHiddenDuplicateMeals } from '@cf-shared/data-model/log-meal';
import { createDayStartEntry, Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { backCompatWelcomePhase, FitnessPlan, WelcomePhase, IntensityLevel, planIsBuiltButHasNoExercises, stripGeneratedPlanBackupForJournal } from '@cf-shared/data-model/fitness-plan';
import { OneMuscleGrowthValue } from '@cf-shared/data-model/muscle-growth-preference';
import { TrainingLevel } from '@cf-shared/data-model/training-level';
import { DebugService } from 'services/debug.service';
import { actualMealTargets } from '@cf-shared/actual-meal-targets';
import { mealTargetsAsIfEmpty } from '@cf-shared/meal-targets-as-if-empty';
import { IntakeFormState, stripGeneratedPlanBackupsForJournal } from '@cf-shared/data-model/intake-form-state';
import { ClockHourMode, FoodValuesDisplayMode } from './settings.service';
import { CloudFunctionsService } from './cloud-functions.service';
import { paginateDownloadUserDb } from './paginate-download-user-db';
import { decideImpersonatedClientView, ImpersonatedClientViewDecision } from './impersonation-helpers';
import { deletePendingCopyAsync, IMPERSONATION_COPY_PENDING_DELETE_KEY, shouldDiscardFailedDownload } from './impersonation-stop';
import { coachOwnRosterDbName, localUserDbBaseName } from './local-user-db-name';
import { browserLocalStorageOrNull } from 'environments/environment-helpers';
import { LoggingService, errorReportFields, isTransientFetchError } from './logging.service';
import { timeoutCausedByFreeze } from './running-clock';
import { TranslatedString } from 'models/translated-string';
import { CustomExercise, fixupSlotId, isObsoleteSlotId } from './exercise-db.service';
import { planSpuriousSelfForkHeal, deforkSelfFork, selfForkHealDeferReason } from './exercise-fork-update';
import { PlanUpdateOutcome, PlanUpdateParams } from '@cf-shared/plan-update-interface';
import { PlanUpdate } from '@cf-shared/plan-update';
import { PouchDoc, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { CoachRelationship, coachRelAsCoachId, coachRelAsClientId, CoachClientSlot, CoachClientStatus } from '@cf-shared/data-model/coach';
import { ImportedGlobalExercisesIds, importedGlobalExercisesIdsId } from '@cf-shared/data-model/exercise';
import { declareEvent, PubSubService } from './pubsub.service';
import { DayTaskStatusService } from './day-task-status.service';

// Event emitted when a log entry is saved
export const logEntrySavedEvent = declareEvent<Log_Entry>('logEntrySaved');
import { FormatNutrients } from 'util/formatting';
import { gcOrphanSubslots } from 'util/orphan-subslot-cleanup';
import { describeOrphanSubSlotsAtSave, PlanSaveContext } from 'util/orphan-subslot-at-save';
import { reportMissingSubSlotsHealedAtLoad } from 'util/orphan-subslot-reporter';
import { normalizeProgramBackupAtLoad } from 'util/program-backup-normalize';
import { fillMissingIntensities, repairObsoleteSubSlotIds, repairProgramFormatAtLoad } from 'util/program-format-repair';
import { repairMealIngredientsFromStorage } from 'util/meal-ingredients';
import {
  PlanRecoveryCandidate,
  PlanRecoverySource,
  pickBestPlanRecoveryCandidate,
  planBackupAsRecoveryCandidate,
  rebuildTrainingStructureFromWorkoutLogs,
} from '@cf-shared/data-model/fitness-plan-recovery';
import { restoreTrainingProgram } from 'services/exercise-switching';
import { logIdsIncludeWorkout } from 'services/workout-plan-reference-decisions';
import { parseUrlToPages } from 'utils/url-parser';
import { TrainingGoal, NutritionLabelStyle } from '@cf-shared/enums';
import { createWeeklyCheckupEntry, Log_WeeklyCheckup } from '@cf-shared/data-model/log-weekly-checkup';
import { AuthRole } from '@cf-shared/couch-auth';
import { LocaleSettings, sleep, timeout, TimeoutError } from '@cf-shared/shared-util';
import { Mutex } from '@cf-shared/mutex';
import { WorkoutHistory } from '@cf-shared/review-prompt-gate';
import { reviewPromptHistoryKeyRange, summarizeWorkoutHistory } from '@cf-shared/review-prompt-engagement';
import { awaitSignalOrTimeoutAsync, FirstProbeGateOutcome } from './first-probe-gate';
import { migrateLegacyFitnessWeightUnit, migrateOriginalTargetRepsToExerciseStat, recoverMissingFitnessWeight, repairImplausibleCustomWeightIncrements } from './user-db.migrations';
import { DayTaskStatuses, DAY_TASK_STATUS_CACHE_ID, TaskStatus } from '@cf-shared/data-model/day-task-status-cache';
import { BacklogCache, mergeLogEntries } from './backlog-cache';
import { isDatabaseUnavailable, isDatabaseTornDown } from './database-unavailable-error';

declare function emit(key: any, value?: any): void;

/**
 * How long a failed start-up waits for the database it is abandoning to close, in
 * milliseconds (df-92f044df97ec). Deliberately the same figure the sign-out teardown gives
 * each of its steps (`SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS`, `#4762`), and for the same reason:
 * this is a backstop against a wedged local store, not a budget — a healthy close finishes
 * in well under a second. See `UserDb.closeAbandonedStartupDatabaseAsync`.
 */
const ABANDONED_STARTUP_CLOSE_TIMEOUT_MS = 2_000;

/**
 * The problem name a failed or given-up abandon-close is reported under. Keep it stable —
 * it is the grouping key a reader searches Sentry for.
 */
const ABANDONED_STARTUP_CLOSE_PROBLEM = 'AbandonedStartupDatabaseCloseFailed';

/** Which of the two start-ups abandoned the database, reported alongside a failed close. */
enum AbandonedStartup {
  /** `initializeAsync` — opens the local store AND connects the remote sync. */
  Cold = 'cold',
  /** `initializeLocalOnlyAsync` — the warm resume, local store only, no network. */
  Warm = 'warm',
}

// Settings interface for storing user settings in database
export interface Settings extends PouchDoc {
  clockHourMode: ClockHourMode;
  nutritionLabelStyle?: NutritionLabelStyle;
  foodValuesDisplayMode?: FoodValuesDisplayMode;
  cardAnimations: boolean;
  testMode: boolean;
  progressionTesting: boolean;
  progressionTesting_Slot: string;
  progressionTesting_Sets?: number;
  progressionTesting_Level: TrainingLevel;
  progressionTesting_otherLevel: TrainingLevel;
  progressionTesting_Intensity: IntensityLevel;
  progressionTesting_SlotIntensities: string;
  debugFoodSearch: boolean;
  cachedRole: AuthRole;
  cachedRoleExpiry_utc: string;
  unitsPrefsJSON: string;
  hintsSeenJSON: string;
  helpSeenJSON: string;
  debugShowAllHints: boolean;
  debugShowAllTutorials: boolean;
  locale: LocaleSettings;
  currentWOSolverTestId: string;
  usedCommunityBefore: boolean;
  /** Sticky "logged food at least once" marker — see SettingsService (#4325). */
  hasLoggedFoodEver: boolean;
  /**
   * #4625 — when we last asked this person to rate the app, and when they last
   * told us something was wrong. Optional because every settings document
   * written before this shipped has neither.
   *
   * **Neither is copied plainly in either direction** — see `SettingsService`
   * and `review-prompt-settings.ts`. This document is last-writer-wins, and a
   * lost cap marker means somebody is asked to rate the app twice.
   */
  reviewPromptLastAskedAt_utc?: string;
  reviewPromptLastProblemReportedAt_utc?: string;
  /** #4816 — every ask, merged as a union. Absent on documents written before phase 2. */
  reviewPromptAskedAt_utc?: string[];
  /**
   * #4963 phase 3 — was this user invited by somebody, and has their training
   * flag been recorded. Optional because every settings document written before
   * this shipped has neither.
   *
   * **Neither is copied plainly in either direction** — see `referral-settings.ts`.
   */
  referralWasReferred?: boolean;
  referralTrainingReported?: boolean;
  showReportBugFAB2: boolean;
  barcodeCameraId: string;
  barcodeCameraZoom: number | null;
  barcodeCameraFunctional: boolean;
  showDayStartEvents: boolean;
  showDayEndEvents: boolean;
  notificationsWorkout?: boolean;
  notificationsMeals?: boolean;
  notificationsWeighIn?: boolean;
  notificationsWeeklyCheckup?: boolean;
  notificationsSleep?: boolean;
  migrated?: boolean; // Track if settings have been migrated from localStorage
}

// Create FoodHeader from Food. Normally, we use FoodHeader for faster download of searches, but sometimes we already have the full Food object
export function FoodToHeader(food: Food): FoodHeader {
  const header: FoodHeader = {
    _id: food._id,
    name: food.name,
    score: undefined,
    elasticHit: null,
    cachedFood: food,
    displayNutrients: FormatNutrients(food.nutrients),
  };
  return header;
}

// return true if the array contains any repeated id (used for subSlotIds heal + canary)
function hasDuplicateIds(ids: string[]): boolean {
  return ids.length !== new Set(ids).size;
}

/**
 * The workouts a `workoutByExerciseId` read returned, each once, in the order the index gave them.
 *
 * That index emits one row per workout ENTRY, so a workout holding the same exercise twice comes
 * back twice — which an exercise changed after sets were logged, then changed back, produces
 * (#5507). Rows with no document (deleted between the index build and the read) are dropped.
 */
function uniqueWorkoutDocs(rows: { doc: Log_Workout }[]): Log_Workout[] {
  const seen = new Set<string>();
  const docs: Log_Workout[] = [];
  // for each row, keep the first time its workout appears
  for (const row of rows) {
    if (!row.doc || seen.has(row.doc._id)) continue;
    seen.add(row.doc._id);
    docs.push(row.doc);
  }
  return docs;
}

const debugStateId = 'debugstate';
// exported because the coach dashboard fetches these same four documents out of
// somebody else's account, and typing the ids out a second time is how the two
// lists come apart under a rename
export const immutableStateId = 'immutable';
const dayTaskStatusCacheId = DAY_TASK_STATUS_CACHE_ID;
export const declaredStateId = 'declared';
export const currentPlanId = 'currentplan';

// #4587: how far back the plan repair looks when a built plan has lost every exercise.
// Both bound a search that only ever runs on an already-broken plan, so they trade a
// little reading against the chance of finding a copy worth restoring.
//
// Plan backups: the newest ones can themselves be damaged — the plan was journalled after
// it was emptied as well as before — so the search must be able to read past a few.
const PLAN_BACKUP_RECOVERY_SCAN_LIMIT = 20;
// Workout logs: enough to hold several passes through every day of a split, so the newest
// log for each day is reachable even when the user trains one day far more than the others.
const WORKOUT_LOG_RECOVERY_SCAN_LIMIT = 60;
export const fitnessStateId = 'fitness';
const scheduleId = 'schedule';
const customMeasuresId = 'measures';
const intakeFormId = 'intakeForm';
const profileStateId = 'profile';
const settingsId = 'settings';

/**
 * A day's cached task status changed — open calendars should redraw.
 *
 * `startDate`/`endDate` bound every changed day. `clusters` (#4451) says where
 * inside that span the changed days actually are, and the sync path sets it: its
 * changed days are scattered across years, so the span alone cannot tell a
 * subscriber whether a wave STRADDLING its visible days has anything on screen.
 * (The span still does most of the filtering — a wave lying entirely in old
 * history fails the overlap test outright.) Publishers whose span already
 * describes one just-saved entry omit `clusters`.
 *
 * Subscribers must test it with `taskStatusChangeAffectsVisibleDays`, which
 * falls back to the bounding span when there are no clusters.
 */
export const TaskStatusCacheUpdatedEvent = declareEvent<{
  startDate: string;
  endDate: string;
  clusters?: ReactiveDayStatusCluster[];
}>('database:taskStatusCacheUpdated');
export const BodyCompositionDataChangedEvent = declareEvent<{}>('database:bodyCompositionDataChanged');

export type FitnessPlanWithTimestamp = FitnessPlan & {
  timestamp_utc: string;
};

export interface CoachManagingClient {
  clientUid: string;
  clientDisplayName: string;
}

// L6/S2 (#3986): one entry of the boot-singleton batch read. The generic ties `defaultDoc`,
// `updater`, and `assign` to the SAME document type, so a mismatched entry (e.g. a Settings
// assign paired with a FitnessState default) fails to compile.
interface SingletonSpec<T> {
  id: string;
  defaultDoc: T;
  updater?: (obj: T) => Promise<void>;
  assign?: (doc: T) => void;
}

// identity helper that infers T from the entry, so each spec is fully type-checked at its
// construction site before it is erased into the heterogeneous `SingletonSpec<any>[]` list
// (a plain array literal cannot carry a different T per element).
function singletonSpec<T>(spec: SingletonSpec<T>): SingletonSpec<any> {
  return spec;
}

/**
 * The documents one start-up loaded, before any of them is served to the app (#4809).
 *
 * They travel together on purpose. `UserDb` used to set each field the moment its document
 * arrived, which meant that part-way through a load the app could read one account's
 * document alongside another account's database. They are now handed over in a single step
 * by `adoptLoadedDatabase`.
 *
 * Every field is optional because a load that threw part-way through has filled in only
 * some of them, and the type should say so rather than promise documents that are not there.
 */
interface LoadedUserSingletons {
  settings?: Settings;
  immutableState?: ImmutableState;
  declaredState?: DeclaredState;
  fitnessState?: FitnessState;
  schedule?: Schedule;
  fitnessPlan?: FitnessPlan;
  /** #6248: the singleton ids start-up could not read from the server OR from this device.
   *  Each holds only an empty default, so the cold start must not carry on with them. */
  unreadSingletonIds: string[];
}

/** #6248: how long the start-up waits for the singleton pre-sync before it reads a dropped
 *  document from this device's own database. Ends early the moment that sync finishes. */
const BOOT_SINGLETON_PRESYNC_WAIT_MS = 15_000;

@Injectable()
export class UserDb {
  public database: DatabaseWrapper = null;
  /**
   * #6248: the singleton documents the last start-up could not read from the server or from
   * this device, so they hold only an empty default. Empty in every ordinary start-up. When it
   * is not, the cold start holds the loading screen instead of showing the app.
   */
  public unreadBootSingletonIds: string[] = [];

  // these are quick-access flags that are read from the DeclaredState. they are here so that OneNutrient doesn't need to load this asynchronously
  public nutritionTracking = true;
  public nutritionTargets = true;
  public macrosFlexible = true;
  public calorieCycling = false;

  // #4809: the queue the two start-ups and the shutdown take turns in, so a second start-up
  // waits for the first rather than handing out a database whose documents are still loading.
  private initMutex = new Mutex('UserDb.init');
  // shutdowns still waiting for `initMutex`, for `describeShutdown` (df-5c749cefc673)
  private shutdownsQueuedOnInitMutex = 0;

  immutableState: ImmutableState;
  declaredState: DeclaredState;
  fitnessState: FitnessState;
  schedule: Schedule;
  fitnessPlan: FitnessPlan;
  settings: Settings;

  backlogCache: BacklogCache;

  // Persistent watcher on the `schedule` singleton. Fires only when a schedule change syncs in
  // from another device (never on local saves), so a corrected schedule triggers a backlog
  // rebuild against it. See design-docs/sync-integrity.md.
  private scheduleWatcher: DocWatcher | null = null;

  // The past days whose new entries the backlog rebuild kept in memory because this session's
  // sync had not succeeded yet; rebuilt and saved once it has (#6280). One per database, set up in
  // adoptLoadedDatabase. See design-docs/sync-integrity.md.
  private pastBackfillHold: PastBackfillHold | null = null;

  // The day (today) whose unsaved entries the home page's re-save held back because this session's
  // sync had not succeeded yet; re-saved once it has (#6828). Set up and torn down beside
  // pastBackfillHold. See design-docs/sync-integrity.md.
  private todayResaveHold: PastBackfillHold | null = null;

  // Per-session dedup for the schedule-regen rescue diagnostic — the event-type keys already
  // reported this session, so a lingering collision that re-triggers on every backlog rebuild
  // reports at most once per event type. See reportRescuedCompletedEntries.
  private reportedRescuedTypes = new Set<LogRecordType>();

  // natural ids already reported this session as held by another entry (#4641). A fresh empty
  // meal is not saved, so every session rebuilds it and would report it again. See
  // reportReissuedEntryIds.
  private reportedReissuedNaturalIds = new Set<string>();

  // days already reported as holding two documents for one meal slot, keyed by day plus the
  // dropped ids. See reportDroppedDuplicateMeals.
  private reportedDuplicateMealDays = new Set<string>();

  // the same, for the once-a-day events rather than for meals: documents already reported as the
  // loser of a day holding two for one event, one key per document rather than one per set. See
  // keepOneEntryPerSingletonSlot.
  private reportedDuplicateSingletonDays = new Set<string>();

  // ids of meals already reported this session as having arrived from storage with no
  // ingredient list. Every range read re-reads the same document off disk as a fresh object,
  // so without this the repair would report again on every backlog rebuild. Read and written
  // by repairMealIngredientsFromStorage, which backCompatMealEntry hands it to.
  private reportedMealsWithNoIngredientList = new Set<string>();

  // the coach-managing-client and impersonation keys are read from localStorage on almost every
  // navigation, so a read that fails is reported once per page load, not once per screen (#5191)
  private readonly reportStoredContextReadOnce = createOncePerConditionReporter(
    (conditionName, err) => this.logging.trackSilentProblem(conditionName, errorReportFields(err)),
  );

  // track pending cache operations to prevent concurrent cacheFoodAsync calls for the same food ID
  private pendingCaches: Map<string, Promise<Food>> = new Map();

  constructor(
    private cloudFunctions: CloudFunctionsService,
    private dbProvider: DatabaseWrapperProvider,
    public logging: LoggingService,
    public debug: DebugService,
    private pubSub: PubSubService,
    // The day-task-status cache and its reactive recompute engine. It used to be
    // 1646 lines of this class and shared none of its data; df-3b8918aefa6a moved
    // it out. UserDb keeps forwarding methods so pages call it exactly as before.
    private dayTaskStatus: DayTaskStatusService,
  ) {
    this.backlogCache = new BacklogCache({
      generateBacklogInMemoryAsync: this.generateBacklogInMemoryAsync.bind(this),
      getDatabase: () => this.database,
      logging: this.logging,
    });
  }

  /** Undo a FAILED impersonation download (#6448). Never changes where this session writes: an
   * officer already impersonating someone can start a second impersonation from Admin Tools, and
   * pointing that session back at the officer's own database is the leak #6448 fixed. So this only
   * deletes the partial copy (unless it is the copy in use, see `shouldDiscardFailedDownload`) and
   * points `impersonationDb` back at whatever the session is routed to.
   *
   * The partial copy is NOT `DatabaseWrapper.localDb`, so this is one of the destroy sites
   * deliberately outside the `beginPurge` bracket — why that is safe is written once, after
   * `endPurge` in `database-recovery.service.ts`. Nothing is outstanding against it:
   * `paginateDownloadUserDb` awaits every `put` in turn, and nothing reads through it. */
  async discardFailedImpersonationDownloadAsync() {
    const partial = this.database.impersonationDb;
    const inUse = this.database.getTemporaryDb();
    this.database.impersonationDb = inUse;
    if (shouldDiscardFailedDownload(partial?.name, inUse?.name)) {
      await partial.destroy();
    }
  }

  /** #6448: record the temporary impersonation copy's name, so the next start deletes it once
   *  nothing that read from it is alive. */
  markImpersonationCopyForDeletion() {
    const name: string | undefined = this.database?.impersonationDb?.name;
    if (!name) {
      // start-up routing always sets the copy while impersonating, so this should not happen; if it
      // does, the copy stays on this device until the app is reinstalled
      this.logging.trackSilentProblem('ImpersonationCopyNameUnknown', { hasDatabase: !!this.database });
      return;
    }
    try {
      localStorage.setItem(IMPERSONATION_COPY_PENDING_DELETE_KEY, name);
    } catch (e) {
      // the copy stays on this device; it is never synced anywhere, so it is clutter, not a leak
      this.logging.trackSilentProblem('ImpersonationCopyMarkFailed', errorReportFields(e));
    }
  }

  /** #6448: delete the copy a stopped impersonation left, before this start opens any database.
   *  The signed-in user's own local database has the same name shape, so it is always among the
   *  names kept. The rules and their tests are in impersonation-stop.ts. The names are built
   *  with the same helper the open paths use, because a copy kept only when its name matches
   *  character for character cannot survive the two drifting apart. */
  private async deletePendingImpersonationCopyAsync(signedInUid: string) {
    const uids = [signedInUid, this.getImpersonationUserAuth()?.uid, this.getCoachManagingClient()?.clientUid];
    await deletePendingCopyAsync({
      readMarker: () => localStorage.getItem(IMPERSONATION_COPY_PENDING_DELETE_KEY),
      removeMarker: () => localStorage.removeItem(IMPERSONATION_COPY_PENDING_DELETE_KEY),
      namesThisStartUses: uids.filter(uid => !!uid).map(uid => localUserDbBaseName(uid)),
      destroyDatabaseAsync: name => this.dbProvider.createOrLoadDatabase(name).destroy(),
      reportProblem: (name, err) => this.logging.trackSilentProblem(name, errorReportFields(err)),
    });
  }

  getImpersonationUserAuth(): UserAuth | null {
    return readStoredJson<UserAuth>(localStorage, 'impersonationUserAuth', 'ImpersonationStorageReadFailed', this.reportStoredContextReadOnce);
  }

  /** True when an admin/support account is in an impersonation session. Used to
   *  decide whether a managed coach's client must be loaded as a read-only local
   *  snapshot instead of a live sync (#4192). */
  isImpersonating(): boolean {
    return !!this.getImpersonationUserAuth();
  }

  getCoachManagingClient(): CoachManagingClient | null {
    return readStoredJson<CoachManagingClient>(localStorage, 'coachManagingClient', 'CoachContextStorageReadFailed', this.reportStoredContextReadOnce);
  }

  setCoachManagingClient(client: CoachManagingClient | null) {
    try {
      if (client) {
        localStorage.setItem('coachManagingClient', JSON.stringify(client));
      } else {
        localStorage.removeItem('coachManagingClient');
      }
      // force cold start on next boot — entering or exiting coach-client
      // context switches to a different PouchDB that may not be synced locally
      clearWarmResumeFlag();
    } catch (e) {
      // the coach entering or leaving a client's context did not stick; one per tap, so not gated
      this.logging.trackSilentProblem('CoachContextStorageWriteFailed', { ...errorReportFields(e), entering: !!client });
    }
  }

  setImpersonationUserAuth(auth: UserAuth | null) {
    try {
      if (auth) {
        localStorage.setItem('impersonationUserAuth', JSON.stringify(auth));
      } else {
        localStorage.removeItem('impersonationUserAuth');
      }
      // force cold start on next boot — impersonation switches to a different
      // user's PouchDB that may not be synced locally
      clearWarmResumeFlag();
    } catch (e) {
      // the admin starting or ending an impersonation did not stick; one per tap, so not gated
      this.logging.trackSilentProblem('ImpersonationStorageWriteFailed', { ...errorReportFields(e), entering: !!auth });
    }
  }

  // Download a user's CouchDB into a local, read-only impersonation PouchDB
  // (`userdb-fr_<uid>`) using the officer-gated server download (admin creds),
  // which can read ANY user's DB regardless of CouchDB membership. Shared by
  // admin impersonation of a user AND by admin/support opening an impersonated
  // coach's client as a read-only snapshot (#4192 — the admin is not a member of
  // the client's DB, so a live sync would 403). `uid` is the user/client to load.
  async downloadUserIntoImpersonationDbAsync(uid: string) {
    const db = this.dbProvider.createOrLoadDatabase(localUserDbBaseName(uid));
    // point impersonationDb at the DB being downloaded so a caller's rollback
    // (admin-user-search's catch → discardFailedImpersonationDownloadAsync) can destroy
    // the partial DB on failure. we do NOT destroy any previously-referenced
    // impersonationDb here: in the coach-client pre-reload flow it is the coach's
    // snapshot, which must survive (a full app reload follows and discards this
    // wrapper, and the coach snapshot is reused if the admin later exits managing
    // back to coach impersonation). Each `userdb-fr_<uid>` persists in IndexedDB
    // by design; leftover snapshots are harmless (see design-docs/coach-mode.md).
    this.database.impersonationDb = db;

    await paginateDownloadUserDb(
      (lastSeenKey) =>
        this.cloudFunctions.downloadUserDbAsync(
          lastSeenKey === undefined ? { userId: uid } : { userId: uid, lastSeenKey },
        ),
      (doc) => db.put(doc, { force: true }),
    );
  }

  async loadImpersonatedUserDataAsync() {
    const impersonationUserAuth = this.getImpersonationUserAuth();
    if (!impersonationUserAuth) {
      return;
    }
    await this.downloadUserIntoImpersonationDbAsync(impersonationUserAuth.uid);
  }

  // Route reads/writes to the local `userdb-fr_<uid>` impersonation snapshot as
  // the primary (temporary) DB. Opens it if not already open — after a reload
  // the fresh wrapper has no impersonationDb yet, but the data is already in
  // IndexedDB from a prior download.
  // #4809: the same routing against a NAMED database rather than whichever one this service
  // is currently serving. Start-up routes its own new database this way, because it has to
  // happen before the documents are loaded — which is before that database is handed out.
  private switchImpersonationDbOn(database: DatabaseWrapper, uid: string) {
    if (!database.impersonationDb) {
      database.impersonationDb = this.dbProvider.createOrLoadDatabase(localUserDbBaseName(uid));
    }
    database.setTemporaryDb(database.impersonationDb);
  }

  // After the base DB is opened, route reads/writes to the correct temporary DB
  // BEFORE singleton init so singletons load from it, not from the admin's own
  // base DB. Shared by both init paths (cold + warm-resume local-only).
  //
  // #4192: when an admin/support account is impersonating a coach and managing
  // one of that coach's clients, read from the client's read-only local snapshot
  // (downloaded server-side with admin creds — see downloadUserIntoImpersonationDbAsync).
  // The admin is NOT a CouchDB member of the client's DB, so the normal live-sync
  // manage mode would 403 and boot fatally; the init caller passes NO
  // managedClientUid in this case so no client-DB remote sync is attempted.
  // Otherwise fall back to normal impersonation switching (a no-op for a real
  // coach in live manage mode or a normal signed-in user).
  private routeInitialImpersonationDb(
    database: DatabaseWrapper, clientView: ImpersonatedClientViewDecision,
    managingClient: CoachManagingClient | null,
  ) {
    if (clientView.useSnapshot) {
      this.switchImpersonationDbOn(database, clientView.clientUid);
    } else {
      this.setupImpersonationOn(database, managingClient);
    }
  }

  private static readonly singletonIds = [debugStateId, immutableStateId, declaredStateId, fitnessStateId, scheduleId, currentPlanId, customMeasuresId, intakeFormId, settingsId, dayTaskStatusCacheId];

  private registerQueries(database: DatabaseWrapper) {
    // tslint:disable-next-line: only-arrow-functions
    let workoutByExerciseId = function (entry: Log_Entry) {
      if (entry.type === 'Workout') {
        for (var i = 0; i < entry.exercises.length; i++) {
          var e = entry.exercises[i];
          emit(e.exerciseId, null);
        }
      }
    };

    database.createQuery('workoutByExerciseId', workoutByExerciseId);

    const planBackupById = function (entry: Log_Entry) {
      if (entry._id.endsWith('-FitnessPlan') &&
        (entry as any as FitnessPlan).welcomePhase === 'done' &&
        (entry as any as FitnessPlan).ready) {
        emit(entry._id, null);
      }
    }
    database.createQuery('planBackupById', planBackupById);

    // #6428: a coach's saved changes to one exercise, by sub-slot and time. indexOf rather than an
    // end match, because createUniqueJournalEntryAsync appends _1, _2 to a colliding id. The
    // documents carry no `type`, so no day-log view picks them up
    const exerciseChangeBySubSlot = function (entry: any) {
      if (entry._id.indexOf('log/') === 0 && entry._id.indexOf('-ExerciseChange') > 0 && entry.subSlotId) {
        emit([entry.subSlotId, entry.changed_utc], null);
      }
    }
    database.createQuery('exerciseChangeBySubSlot', exerciseChangeBySubSlot);

    const userFoodsByEAN = function (entry: Food) {
      if (entry._id.startsWith('food/') && entry.EANs && entry.EANs.length) {
        for (var i = 0; i < entry.EANs.length; i++) {
          emit(entry.EANs[i]);
        }
      }
    }
    database.createQuery('userFoodsByEAN', userFoodsByEAN);

    // special filter only for foods that were used/favorited, so we can speed up the default search
    const userFoodsUsed = function (entry: Food) {
      if (entry._id.startsWith('food/') && entry.local && (entry.local.lastUsed_utc || entry.local.liked)) {
        emit(entry._id, null);
      }
    }
    database.createQuery('userFoodsUsed', userFoodsUsed);

    // Query for getting log entries by date for task status calculation
    // Since CouchDB views can't use Luxon, we'll emit the entry itself
    // and process dates on the client side using Luxon properly
    const logEntriesForTaskStatus = function (entry: Log_Entry) {
      // Skip deleted entries
      if (entry._deleted) {
        return;
      }

      if (entry._id && entry._id.startsWith('log/') &&
        (entry.type === 'Workout' ||
          entry.type === 'Meal' ||
          entry.type === 'WeighIn' ||
          entry.type === 'WeeklyCheckup')) {
        // Emit the time_utc as key so we can query by date range
        // The value contains all the data we need for status calculation
        emit(entry.time_utc, {
          type: entry.type,
          state: entry.state,
          timeOffset_min: entry.timeOffset_min || 0
        });
      }
    };
    database.createQuery('logEntriesForTaskStatus', logEntriesForTaskStatus);

    // view for counting completed workouts (used to hide helper text for experienced users)
    const completedWorkouts = function (entry: Log_Entry) {
      if (entry._id && entry._id.startsWith('log/') && entry.type === 'Workout' && entry.state === 'Done') {
        emit(entry._id);
      }
    };
    database.createQuery('completedWorkouts', completedWorkouts);

    // view for counting completed workouts that contain bodyweight exercises
    const completedBwWorkouts = function (entry: Log_Entry) {
      if (entry._id && entry._id.startsWith('log/') && entry.type === 'Workout' && entry.state === 'Done') {
        for (var i = 0; i < entry.exercises.length; i++) {
          var sets = entry.exercises[i].sets;
          if (!sets) continue;
          for (var j = 0; j < sets.length; j++) {
            var set = sets[j];
            if (set && set.stats && (set.stats.bw_pc || 0) > 0) {
              emit(entry._id);
              return;
            }
          }
        }
      }
    };
    database.createQuery('completedBwWorkouts', completedBwWorkouts);

    // view for counting completed workouts of ONE split day (#4061). A plan is a split:
    // the user rotates through workouts A, B, C, and `planReference` is which of them a
    // workout is (A=0, B=1, ...). The onboarding payoff message is sequenced per split
    // day, because each day gets its own strength test the first time it is trained.
    // The key is emitted as a STRING on purpose: runQueryAsync rejects a falsy startkey,
    // so a numeric 0 -- split day A, the most common case -- would throw.
    // Workouts with no planReference are skipped. saveWorkoutAsync stamps one on every
    // workout it saves, so this only affects hypothetical legacy rows; the cost of a miss
    // is one friendlier-than-generic message, with no data effect.
    const completedWorkoutsByPlanReference = function (entry: Log_Entry) {
      if (entry._id && entry._id.startsWith('log/') && entry.type === 'Workout' && entry.state === 'Done') {
        var planReference = (entry as any as Log_Workout).planReference;
        if (typeof planReference === 'number') {
          emit('' + planReference);
        }
      }
    };
    database.createQuery('completedWorkoutsByPlanReference', completedWorkoutsByPlanReference);

    // view for the review prompt's personal-record moment (#4816): per workout and
    // exercise, the best estimated one-rep max of its finished sets. One small number
    // per row, so finding somebody's best ever needs no workout documents at all.
    // It exists because the stored per-exercise estimate is the LAST session's best
    // since #5174, not the best ever. Every finished set counts here, test sets
    // included: a past test was real strength. Nothing warms this view up — it is
    // built the first time somebody the review-prompt gate already allows finishes a
    // workout, so most devices never build it. See design-docs/review-prompts.md.
    // The strings are LogRecordState.Done and the Workout log type: a view function
    // cannot import an enum.
    const bestStrengthByExercise = function (entry: Log_Entry) {
      if (entry._id && entry._id.startsWith('log/') && entry.type === 'Workout' && entry.exercises) {
        for (var i = 0; i < entry.exercises.length; i++) {
          var exercise = entry.exercises[i];
          if (!exercise || !exercise.exerciseId || !exercise.sets) continue;
          var best = 0;
          for (var j = 0; j < exercise.sets.length; j++) {
            var set = exercise.sets[j];
            var estimate = set && set.state === 'Done' && set.stats ? set.stats.estimated1rm_net_kg : 0;
            if (typeof estimate === 'number' && isFinite(estimate) && estimate > best) best = estimate;
          }
          if (best > 0) emit(exercise.exerciseId, best);
        }
      }
    };
    database.createQuery('bestStrengthByExercise', bestStrengthByExercise);
  }

  // #4587: whether this session has already tried to repair a plan that was found built
  // but holding no exercises. The repair reads documents and reports, and a damaged plan
  // is re-read on every page that loads it, so the attempt is bounded to one per session;
  // a failed READ resets it, because nothing was actually looked at. Not persisted — a
  // restart is exactly when looking again is worthwhile, since the copy the repair needs
  // may only have arrived by sync in the meantime.
  private planRepairAttempted = false;

  /** #4587: how many days a week the user trains, or `undefined` when we cannot tell.
   *
   *  `getWorkoutsPerWeek` answers 0 both for a missing declared state and for one whose
   *  `trainingDays` is absent, and 0 is the one value that switches the empty-plan
   *  protection OFF — so handing its answer straight on would turn "we do not know" into
   *  "this user does not train", which is the reading that loses the data.
   *  `planIsBuiltButHasNoExercises` treats `undefined` as "assume they train", and that is
   *  what an unknown must map to.
   *
   *  Both halves matter, and the second is the common one: `getEmptyDeclaredState` builds
   *  the default document as `{}` with no `trainingDays` field at all, and that default is
   *  what gets assigned whenever the user has no declared-state document. Checking only
   *  that the object exists would leave every one of those users with no repair and no
   *  rebuild warning — the two things this whole change adds. */
  private workoutsPerWeekOrUnknown(): number | undefined {
    return this.declaredState?.trainingDays ? getWorkoutsPerWeek(this.declaredState) : undefined;
  }

  /** #4587: whether this session has already reported that a plan repair could not read
   *  what it needed. Separate from `planRepairAttempted`, which a failed read deliberately
   *  resets so the next load retries: without this, a database that stays unavailable would
   *  re-report on every plan load, which is the same flood the attempt flag exists to stop. */
  private planRepairReadFailureReported = false;

  // set by the updateFitnessState closure inside initSingletonsAsync when
  // migrateLegacyFitnessWeightUnit mutates docs during init. the init entry points flush these
  // saves after init completes so the cleanup persists even in sessions that wouldn't otherwise
  // write fitness or settings.
  private hasPendingMigrationSaves = false;

  // set by the updateFitnessPlan closure when migrateOriginalTargetRepsToExerciseStat
  // mutates the fitness plan. distinct from hasPendingMigrationSaves because the plan needs
  // its own save call appended to the flush — without this, the migration's
  // `delete path.originalTargetReps` lives only in memory and re-runs on every login forever.
  private hasPendingPlanMigrationSaves = false;

  // #3808 — guards the spurious-self-fork heal so it scans the user's customs at most once
  // per session (the scan is cheap and writes only when it finds a fork to collapse).
  private spuriousSelfForkHealDone = false;

  // O1 (#3986): the singleton load is its own boot phase — it was the
  // dominant cold:userDb.init cost before the L6/S2 batch, and the phase
  // timing proves (or disproves) that the batch actually fixed it in prod.
  // Runs on both the cold and the warm init path. end() sits in a finally so
  // a throw (for example the DB-recovery path) still closes the phase; the
  // actual work lives in initSingletonsInnerAsync below.
  private async initSingletonsAsync(database: DatabaseWrapper): Promise<LoadedUserSingletons> {
    bootTimings.start(BootPhase.Singletons);
    try {
      return await this.initSingletonsInnerAsync(database);
    } finally {
      bootTimings.end(BootPhase.Singletons);
    }
  }

  // does the actual singleton loading: one bounded batch fetch of all boot
  // singletons, then the order-dependent per-singleton post-steps (updater
  // migrations, normalize, cache, collect). It does NOT wire up the schedule watcher or the
  // pulled-doc listener — those moved to `adoptLoadedDatabase` in #4809, and registering
  // them here again would reopen the #4249 bug (see that method).
  // Called only through initSingletonsAsync above, which times it.
  private async initSingletonsInnerAsync(database: DatabaseWrapper): Promise<LoadedUserSingletons> {
    // #4809: the loaded documents are collected here and handed to the caller, which puts
    // them on `this` in the SAME step it hands out the database. They used to be assigned to
    // the fields one at a time as each one arrived, which meant the fields belonged to the
    // new database while `this.database` still pointed at the old one — and a save taken in
    // that window went to the wrong one. See the comment on the adopt block in
    // `initializeAsync`.
    const loaded: LoadedUserSingletons = { unreadSingletonIds: [] };

    const updateFitnessState = async (doc: FitnessState) => {
      if (!doc.allEquipment) {
        doc.allEquipment = {};
      }
      if (doc.weightConfigs) {
        doc.allEquipment = { ...doc.allEquipment, ...doc.weightConfigs };
        delete doc.weightConfigs;
      }
      if (doc.exerciseStats) {
        for (let exerciseId in doc.exerciseStats) {
          const stat = doc.exerciseStats[exerciseId];
          if (stat.weightConfigId && !stat.equipmentId) {
            stat.equipmentId = stat.weightConfigId;
            delete stat.weightConfigId;
          }
        }
      }
      if (doc.oldSubslots) {
        for (const oldSubslot of doc.oldSubslots) {
          oldSubslot.slotId = fixupSlotId(oldSubslot.slotId);
        }
        delete doc.oldSubslots;
      }
      // migrate legacy weight_unit field (see #3099 follow-up) — back-ports to unitsPrefsJSON
      // then strips; flag for post-init save so the cleanup persists even in sessions where
      // nothing else writes fitness or settings.
      if (migrateLegacyFitnessWeightUnit(doc, loaded.settings)) {
        this.hasPendingMigrationSaves = true;
      }
      // #5344: repair stored equipment step sizes that describe no real machine (the leftover of a
      // round weight entered against an odd one). Same post-init save, same reason — without it the
      // repair stays in memory and every read has to redo it. Deliberately a standing normaliser and
      // not a one-shot: older app builds keep writing bad values for as long as they are installed.
      const repairedIncrements = repairImplausibleCustomWeightIncrements(doc);
      if (repairedIncrements.length > 0) {
        this.hasPendingMigrationSaves = true;
        for (const repaired of repairedIncrements) {
          // INFO tier, not PROBLEM: the repair has happened and the save below persists it, so this
          // records a condition that is already resolved rather than one somebody must act on. The
          // read-time heal in UiExercise.checkEquipment reports on the PROBLEM tier for the opposite
          // reason — it patches without persisting. See design-docs/telemetry.md.
          this.logging.trackSilentInfo('workout.equipment-custom-increment-repaired', repaired);
        }
      }
    }

    const updateDeclaredState = async (doc: DeclaredState) => {
      if (doc.nutritionCoaching === undefined) {
        doc.nutritionCoaching = NutritionCoachingType.Default;
      }
      if (doc.nutritionTracking === undefined) {
        doc.nutritionTracking = NutritionTrackingType.Default;
      }
      if (doc.macroTargets === undefined) {
        doc.macroTargets = MacroTargetsType.Exact;
      }
      if (doc.muscleGrowthPreferenceTable && doc.muscleGrowthPreferenceTable['glutemed'] === undefined) {
        doc.muscleGrowthPreferenceTable['glutemed'] = OneMuscleGrowthValue.Maintain;
      }
    }

    const updateFitnessPlan = async (doc: FitnessPlan) => {
      await this.backCompatFitnessPlan(database, doc, loaded.fitnessState);
      // Phase 2 migration: lift Plan_ExercisePath.originalTargetReps onto
      // ExerciseStat.preferRepTargetByIntensity. Runs after fitnessState is loaded (the
      // fitnessState spec is processed before the plan spec, so `loaded.fitnessState` is set by
      // the time updateFitnessPlan fires on the plan singleton). Mutates BOTH docs; both must be flushed via
      // flushPendingMigrationSavesAsync below. Save order Settings → FitnessState → FitnessPlan
      // is deliberate (see comment above flushPendingMigrationSavesAsync).
      // oldSubslots is unconditionally wiped at line ~413 above — nothing to lift from there.
      // LogWorkout.exercises[*].planSubslotBackup is intentionally not migrated (unbounded
      // historical records; post-Phase-2B field-undefined is acceptable display behavior).
      if (migrateOriginalTargetRepsToExerciseStat(doc, loaded.fitnessState)) {
        this.hasPendingMigrationSaves = true;
        this.hasPendingPlanMigrationSaves = true;
      }
    }

    // L6/S2 (#3986): load all the boot singletons in ONE bounded pass-through read instead of
    // 13 sequential remote round-trips. During the pass-through window (initial sync not yet
    // complete) each per-doc read was a remote round trip bounded only by the 90 s fetch
    // timeout, so 13 in a row dominated cold:userDb.init (35% of splash hangs) and cold-native
    // slowness. The batch replaces only the FETCH; every per-singleton post-step (updater
    // migration, deepNormalize, documentCache, collect) and their ORDER are preserved
    // by processing the specs sequentially below. See design-docs/pass-through-db.md and
    // design-docs/boot-sequence.md (L6/S2).
    //
    // The ordering is load-bearing: updateFitnessPlan reads `loaded.fitnessState`, so the
    // fitnessState spec must be processed (and `loaded.fitnessState` filled in) before the
    // fitnessPlan spec runs its updater. The specs array keeps that order; the for-loop awaits
    // each in turn. Nothing here assigns the `this.*` document fields — since #4809 those are
    // all set together at the end, in `adoptLoadedDatabase`.
    const singletonSpecs: SingletonSpec<any>[] = [
      singletonSpec({ id: settingsId, defaultDoc: this.getEmptySettings(), assign: doc => { loaded.settings = doc; } }),
      singletonSpec({ id: debugStateId, defaultDoc: this.getEmptyDebugState() }),
      singletonSpec({ id: immutableStateId, defaultDoc: this.getEmptyImmutableState(), assign: doc => { loaded.immutableState = doc; } }),
      singletonSpec({ id: declaredStateId, defaultDoc: this.getEmptyDeclaredState(), updater: updateDeclaredState, assign: doc => { loaded.declaredState = doc; } }),
      singletonSpec({ id: fitnessStateId, defaultDoc: this.getEmptyFitnessState(), updater: updateFitnessState, assign: doc => { loaded.fitnessState = doc; } }),
      singletonSpec({ id: dayTaskStatusCacheId, defaultDoc: this.dayTaskStatus.getEmptyDayTaskStatusCache() }),
      singletonSpec({ id: scheduleId, defaultDoc: this.getEmptySchedule(), assign: doc => { loaded.schedule = doc; } }),
      singletonSpec({ id: currentPlanId, defaultDoc: this.getEmptyPlan(), updater: updateFitnessPlan, assign: doc => { loaded.fitnessPlan = doc; } }),
      singletonSpec({ id: customMeasuresId, defaultDoc: this.getEmptyCustomMeasures() }),
      singletonSpec({ id: intakeFormId, defaultDoc: this.getEmptyIntakeFormState() }),
      // coach relationship singletons — always initialized so we can check if user is coach/client
      singletonSpec({ id: coachRelAsCoachId, defaultDoc: {} as CoachRelationship }),
      singletonSpec({ id: coachRelAsClientId, defaultDoc: {} as CoachRelationship }),
      // Tracks the global_exercises doc IDs the user has imported via the public-share flow.
      // The coach-exercise-sync service watches this singleton and (re)starts the `byImportedIds`
      // filtered replication when the list changes. Initialized with an empty list so the
      // DocWatcher can fire on first access without the absent-doc race.
      singletonSpec({ id: importedGlobalExercisesIdsId, defaultDoc: { ids: [] } as ImportedGlobalExercisesIds }),
    ];
    // one ordered id list shared by the batch fetch and the init plan, so both are guaranteed to
    // use the identical order (buildSingletonInitPlan pairs plan[i] with singletonSpecs[i]).
    const singletonIds = singletonSpecs.map(s => s.id);

    // One bounded batch fetch. On timeout/failure fetched stays null and every singleton falls
    // back to the per-singleton read path (readBootSingletonAsync) — never defaults-for-all.
    // #4401: bracket the batch read so a hang payload can say whether the boot
    // was stuck HERE or in the fallback below. Only completed phases reach the
    // snapshot, so an open bracket is reported through bootTimings.currentPhase()
    // as `boot_step`.
    const fetched: Map<string, any> | null = await database.withDocWatcherAsync(async batchWatcher => {
      try {
        return await bootTimings.measureAsync(
          BootPhase.SingletonBatchRead,
          () => database.getTypedRecordsByKeysAsync(singletonIds, batchWatcher),
        );
      } catch (err) {
        console.warn('UserDb: batched singleton boot read failed — falling back to per-singleton reads', err);
        // the batch read failing is exactly the boot-slowness signal this PRD wants observable, so
        // surface it (the per-singleton fallback below still emits its own transient-read telemetry,
        // but the batch-level failure itself would otherwise be invisible). L6/S2 (#3986).
        // name/code let Sentry separate a real batch failure from the expected "Database not
        // available (recovering)" wrapper thrown while interceptDatabaseError runs a DB recovery.
        //
        // #6096: a timeout the phone's freeze caused is not a hang. The read still gave up on
        // waking, which is right (the fallback below is a fresh request), so it is reported as
        // info. The fallback reports its own failures, so a real hang after a freeze still shows.
        //
        // #6120: an ordinary dropped request (no signal, app in the background, DNS) is not
        // reported either. The fallback below is a fresh request that recovers from it, and the
        // Sentry transient filter cannot see a trackSilentProblem event, so skip it here.
        const afterFreeze = timeoutCausedByFreeze(err);
        if (afterFreeze) {
          this.logging.trackSilentInfo('SingletonBatchReadTimedOutAfterFreeze', { not_running_ms: afterFreeze.notRunningMs });
        } else if (isTransientFetchError(err)) {
          console.log('UserDb: batched singleton boot read dropped by the network — not reported, the fallback reads recover');
        } else {
          this.logging.trackSilentProblem('SingletonBatchReadFailed', errorReportFields(err));
        }
        return null;
      }
    });

    const plan = buildSingletonInitPlan(singletonIds, fetched);
    // #6248: every READ happens first, and only then every post-step, in the fixed spec order.
    // The split is what lets a document the reads could not get be looked for in this device's
    // own database before anything is processed, so the post-steps and their order are exactly
    // what they would have been had the first read worked.
    const docsById = new Map<string, any>();
    const unreadable: string[] = [];
    // #4401: the fallback path is the slow one — up to 13 sequential per-doc
    // reads, each a pass-through round trip. Bracket it separately from the
    // batch above so a hang payload says which of the two the boot was in.
    // Opened only when at least one step actually needs the fallback, so an
    // open bracket always means the slow path is really running. That condition
    // is why this uses the explicit start/finally form rather than
    // `bootTimings.measureAsync` as the sites around it do — the bracket is
    // conditional and the wrapper's is not.
    const hasFallbackStep = plan.some(step => step.useFallback);
    if (hasFallbackStep) bootTimings.start(BootPhase.SingletonFallback);
    try {
      for (const step of plan) {
        // if the batch answered for this document, its answer stands (null = does not exist)
        if (!step.useFallback) {
          if (step.fetchedDoc != null) docsById.set(step.id, step.fetchedDoc);
          continue;
        }
        const read = await database.readBootSingletonAsync<any>(step.id);
        if (read.kind === BootSingletonReadKind.Found) {
          docsById.set(step.id, read.doc);
        } else if (read.kind === BootSingletonReadKind.Unreadable) {
          unreadable.push(step.id);
        }
      }
    } finally {
      if (hasFallbackStep) bootTimings.end(BootPhase.SingletonFallback);
    }

    // if any read went unanswered, look for those documents in this device's own database
    if (unreadable.length > 0) {
      loaded.unreadSingletonIds = await this.readDroppedSingletonsFromDeviceAsync(database, unreadable, docsById);
    }

    for (const spec of singletonSpecs) {
      const unreadAtBoot = loaded.unreadSingletonIds.includes(spec.id);
      const doc = await database.processFetchedSingletonAsync(
        spec.id, docsById.get(spec.id) ?? null, spec.defaultDoc, spec.updater, unreadAtBoot);
      spec.assign?.(doc);
    }

    // #4809: the schedule watcher and the pulled-document listener used to be wired up here,
    // at the end of the load. They now live in `adoptLoadedDatabase`, which runs after it.
    // That is required, not tidying: the cold path starts syncing before the documents are
    // loaded, so a listener registered here would note days that the reset in the adopt step
    // then wipes — which is exactly the #4249 bug coming back.
    return loaded;
  }

  /**
   * #6248: find the singleton documents whose start-up read was dropped in this device's own
   * database, adding them to `docsById`, and answer the ids still not found.
   *
   * Looks once straight away (a device that has synced before holds them already), then waits
   * up to `BOOT_SINGLETON_PRESYNC_WAIT_MS` for the singleton pre-sync and looks again. A
   * document still absent after a pre-sync that SUCCEEDED does not exist on the server either,
   * so it keeps its default like any new account's. Anything else stays unread, and the cold
   * start holds the loading screen rather than carry on with an empty default. Why there is no
   * later repair to rely on: `design-docs/pass-through-db.md`, "A singleton the boot could not
   * read".
   */
  private async readDroppedSingletonsFromDeviceAsync(
    database: DatabaseWrapper,
    droppedIds: string[],
    docsById: Map<string, any>,
  ): Promise<string[]> {
    const result = await database.withDocWatcherAsync(watcher => readUnreadableSingletonsFromDeviceAsync(droppedIds, {
      readLocalAsync: ids => database.readBootSingletonsFromDeviceAsync(ids, watcher),
      // if there is no server to sync with (a warm, local-only start), no pre-sync can run and
      // waiting would only stall the start for the whole cap
      waitForSingletonPreSyncAsync: async () => database.hasRemoteDatabase()
        ? waitForInitialSyncAsync(
          listener => database.onInitialSingletonSyncSettled(listener),
          InitialSyncWaitFor.AnyOutcome,
          BOOT_SINGLETON_PRESYNC_WAIT_MS,
        )
        : InitialSyncWaitResult.GaveUpWaiting,
    }));
    for (const [id, doc] of result.found) docsById.set(id, doc);

    const context = {
      dropped_count: droppedIds.length,
      device_copy_doc_ids: [...result.found.keys()],
      not_on_server_doc_ids: result.notFound,
      still_unread_doc_ids: result.stillUnreadable,
      presync_wait: result.waitResult ?? 'not-needed',
      ...(result.localReadError != null ? errorReportFields(result.localReadError) : {}),
    };
    if (result.stillUnreadable.length === 0) {
      // INFO tier: the dropped documents were found, so this start-up carries on with real data
      this.logging.trackSilentInfo('Db: boot singletons read from the device copy after a dropped request', context);
    } else {
      // the cold start holds the loading screen and reports the problem itself, once it is sure
      // the page is not simply going away. A console line here, so the log says why.
      console.warn('UserDb: start-up could not read these singleton documents from the server or this device:', context);
    }
    return result.stillUnreadable;
  }

  /**
   * Hand out the database and the documents that were loaded from it — the ONE step at which
   * this service starts serving a different database (#4809).
   *
   * There must be no `await` anywhere in this method. That is the whole point of it: every
   * value set here describes the same account, and anything running in the middle of a
   * half-finished swap would see a mixture of two — which is how a save ends up writing one
   * account's document through another account's database.
   *
   * The order inside is the #4249 rule, tightened: stop the OLD database feeding the day-task
   * cache, swap everything, reset what the previous database left behind, and only then let
   * the NEW database start feeding it.
   *
   * Why the hand-out is here rather than where the database is opened, and the rest of the
   * rules that come with it: `design-docs/boot-sequence.md`, "Start-ups take turns, and hand
   * over once".
   */
  private adoptLoadedDatabase(database: DatabaseWrapper, loaded: LoadedUserSingletons): DatabaseWrapper | null {
    // #4249: stop the OLD wrapper's pulled-doc listener BEFORE swapping the database, so
    // documents it pulls cannot land in the coalescer after the reset below and flush
    // against the new database.
    this.dayTaskStatus.destroyPulledDocListener();

    // handed back so the caller can shut it down — this method must stay synchronous, so it
    // cannot do that itself. See `supersedePreviousDatabase`.
    const superseded = this.database;

    this.database = database;
    this.settings = loaded.settings;
    this.immutableState = loaded.immutableState;
    this.declaredState = loaded.declaredState;
    this.fitnessState = loaded.fitnessState;
    this.schedule = loaded.schedule;
    this.fitnessPlan = loaded.fitnessPlan;
    this.unreadBootSingletonIds = loaded.unreadSingletonIds;

    this.dayTaskStatus.setDatabase(database);
    // the memo holds values computed from the PREVIOUS database instance —
    // a different user or session must never be served them. The epoch bump
    // also makes any in-flight display refresh drop its (old-database) results.
    this.dayTaskStatus.clearDisplayOnlyMemo();
    // #4587: both flags are about the plan of whichever account this service was serving.
    // A different account (or a coach opening a client) gets its own one attempt and its
    // own one report — without this reset a second account in the same app session would
    // silently get no plan repair at all.
    this.planRepairAttempted = false;
    this.planRepairReadFailureReported = false;
    // #4249: a date pending from the PREVIOUS database must never trigger a
    // recompute against this one — that would write another user's statuses
    this.dayTaskStatus.resetReactiveDayStatusState();

    // Watch the schedule singleton so a corrected schedule that syncs in from another device
    // rebuilds the backlog against it. DocWatcher fires only on remote sync changes, never on
    // local saves (local saves already clear the cache via saveScheduleAsync). Without this, a
    // device that regenerated the backlog against a stale schedule would never self-heal once
    // the real schedule arrives. See design-docs/sync-integrity.md "Schedule Regeneration Must
    // Not Delete Completed Log Entries". Destroy any prior watcher first (re-init on account
    // switch / impersonation).
    this.scheduleWatcher?.destroy();
    this.scheduleWatcher = database.createDocWatcher((changes) => this.handleScheduleSyncedChange(changes));
    database.getSingleton(scheduleId, this.scheduleWatcher);

    // #6280: a returning device keeps its past-day backfill in memory until this database's sync
    // has succeeded, then rebuilds those days so they are saved from the confirmed schedule. A
    // fresh hold per database, so days held for the previous one are never rebuilt against this.
    this.pastBackfillHold?.stop();
    this.pastBackfillHold = new PastBackfillHold(
      listener => database.onInitialSyncSettled(listener),
      (first, last) => this.rebuildHeldPastDaysAsync(database, first, last),
      err => {
        // a sign-out or account switch during the rebuild is not a failure: the days are rebuilt
        // the next time this account's backlog is read
        if (isDatabaseTornDown(err)) return;
        this.logging.trackAbnormalException(err);
      },
    );
    // #6828: the home page's re-save of today waits for the same success, so this device's empty
    // copies of today's entries are not saved over copies another device already holds
    this.todayResaveHold?.stop();
    this.todayResaveHold = new PastBackfillHold(
      listener => database.onInitialSyncSettled(listener),
      (first, last) => this.resaveHeldDaysAfterSyncAsync(database, first, last),
      err => {
        // a sign-out or account switch during the re-save is not a failure: the day is re-saved
        // the next time the home page opens it after a successful sync
        if (isDatabaseTornDown(err)) return;
        this.logging.trackAbnormalException(err);
      },
    );

    // #4249: react to log entries pulled from another device. Until this hook
    // existed, nothing recomputed a day's dot when its data arrived over sync —
    // a workout completed on the phone never updated the tablet's calendar, and
    // the stale cached value was served forever. Re-registered on account switch
    // and impersonation, like the schedule watcher above.
    this.dayTaskStatus.registerPulledDocListener(database);

    // fresh rescue-diagnostic dedup window per account / session (re-init on account switch)
    this.reportedRescuedTypes.clear();
    this.reportedReissuedNaturalIds.clear();
    this.reportedDuplicateMealDays.clear();
    this.reportedDuplicateSingletonDays.clear();
    this.reportedMealsWithNoIngredientList.clear();

    return superseded;
  }

  /**
   * Shut down the wrapper a start-up has just replaced.
   *
   * Until this existed nothing closed it: it stayed in `dbProvider.activeDatabases` with
   * `isUserDb()` true, so every sweep that loops that list went on acting on it, it could
   * still write `databaseStates['user']` over the live wrapper's key, and
   * `attemptGracefulRecoveryAsync` could revive it. The route that gets there is the
   * sign-in retry: `logonChangedAsync` void-calls `postLoginAsync`, and
   * `retryCouchLoginAsync` re-emits on success so the cold start runs again with a server
   * session — a second start-up whose hand-over replaces the first one's wrapper.
   *
   * **Deliberately not awaited.** `shutdownAsync` waits on the wrapper's read-write lock,
   * which waits for that wrapper's in-flight readers — and the wrapper being replaced is
   * exactly the one most likely to be stuck, because a wedged first start-up is why a
   * second one is running at all. Awaiting it would hold `initMutex` for as long as that
   * lasts and hang the boot on the splash. Nothing is lost by not waiting: the part that
   * has to happen now happens synchronously inside `shutdownAsync` — the sync is cancelled
   * and the read-write lock's shutting-down flag is set, which is what takes the wrapper
   * out of `activeDatabases`.
   *
   * A failure here is reported rather than surfaced: the start-up it belongs to has already
   * succeeded, and the user has nothing to do about a database they no longer use.
   */
  private supersedePreviousDatabase(superseded: DatabaseWrapper | null): void {
    if (superseded == null) return;
    void superseded.shutdownAsync().catch(err => {
      this.logging.trackSilentProblem('SupersededDatabaseShutdownFailed', errorReportFields(err));
    });
  }

  // A schedule change synced in from another device — rebuild the backlog against the corrected
  // schedule so each day's entries land on the right slots and stale empty placeholders are
  // cleaned up (data-bearing entries are preserved by generateScheduleLogEntries).
  private handleScheduleSyncedChange(changes: DocChange[]) {
    if (!changes.some(c => c.id === scheduleId)) return;
    this.clearBacklogCacheAsync().catch(err => this.logging.trackAbnormalException(err));
  }

  // Surface the schedule-regen "rescue" diagnostic to Sentry: each entry here is a completed log
  // entry the sweep saved from deletion (the reschedule-collision this fix guards against, caught
  // and working). Deduped to at most one report per event type per session so a lingering case
  // that re-triggers on every backlog rebuild does not flood telemetry. Context carries no user
  // data — only counts, types, and a sample id. See design-docs/sync-integrity.md.
  private reportRescuedCompletedEntries(rescued: RescuedLogEntryInfo[]) {
    if (rescued.length === 0) return;
    const newTypes = [...new Set(rescued.map(e => e.type))].filter(t => !this.reportedRescuedTypes.has(t));
    if (newTypes.length === 0) return;
    for (const t of newTypes) this.reportedRescuedTypes.add(t);
    this.logging.trackSilentInfo('schedule-regen-rescued-completed-entry', {
      count: rescued.length,
      types: newTypes,
      sampleId: rescued[0].id,
    });
  }

  // Surface the schedule-regen id repair (#4641): each entry here is a fresh meal whose natural id
  // was held by a moved meal, so the rebuild gave it the next free id instead. The repair has
  // already run, so this is info, not a problem. Deduped per natural id per session, because the
  // fresh meal is an unsaved placeholder that every session rebuilds. Ids only, no user data.
  private reportReissuedEntryIds(reissued: ReissuedLogEntryIdInfo[]) {
    const unreported = reissued.filter(e => !this.reportedReissuedNaturalIds.has(e.naturalId));
    if (unreported.length === 0) return;
    for (const e of unreported) this.reportedReissuedNaturalIds.add(e.naturalId);
    this.logging.trackSilentInfo('schedule-regen-meal-id-reissued', {
      count: unreported.length,
      sampleNaturalId: unreported[0].naturalId,
      sampleId: unreported[0].id,
    });
  }

  // Normal impersonation routing (a no-op for a real coach in live manage mode or a normal
  // signed-in user), against a NAMED database — see `switchImpersonationDbOn` for why
  // start-up cannot route the database it is currently serving.
  private setupImpersonationOn(database: DatabaseWrapper, managingClient: CoachManagingClient | null) {
    if (!managingClient) {
      const impersonationUserAuth = this.getImpersonationUserAuth();
      if (impersonationUserAuth) {
        this.switchImpersonationDbOn(database, impersonationUserAuth.uid);
      }
    }
  }

  // persist doc mutations made during singleton init by back-compat migrations. called by both
  // initialization paths so the cleanup survives sessions where nothing else writes these docs.
  //
  // Save order: Settings → FitnessState → FitnessPlan. This is deliberate and must not be
  // reversed:
  //   - migrateLegacyFitnessWeightUnit (Phase: pre-#3099) writes Settings + FitnessState. If
  //     Settings fails, fitness still carries weight_unit and the migration re-runs next init —
  //     reversing the order would lose the back-port permanently because the migration's entry
  //     guard skips docs without weight_unit.
  //   - migrateOriginalTargetRepsToExerciseStat (Phase 2) writes FitnessState + FitnessPlan. If
  //     FitnessPlan fails after FitnessState succeeded, the user's CouchDB has populated
  //     preferRepTargetByIntensity AND a path that still carries originalTargetReps. Next login:
  //     the idempotency guard sees the preference present, skips the lift, but still runs
  //     `delete path.originalTargetReps` and re-flags hasPendingPlanMigrationSaves. Eventual
  //     consistency, no data loss. Reversing the order would mean a Plan-saved-then-FitnessState-
  //     failed window where path data is gone but the lift was never persisted → permanent loss.
  //
  // Swallows errors — both migrations are cleanup, not correctness preconditions, so a transient
  // save failure must not block login.
  private async flushPendingMigrationSavesAsync(): Promise<void> {
    if (!this.hasPendingMigrationSaves && !this.hasPendingPlanMigrationSaves) return;
    const planNeedsSave = this.hasPendingPlanMigrationSaves;
    this.hasPendingMigrationSaves = false;
    this.hasPendingPlanMigrationSaves = false;
    const tx = DatabaseWrapper.getTransactionId();
    try {
      await this.saveSettingsAsync(tx);
      await this.saveFitnessStateAsync(tx);
      if (planNeedsSave) await this.savePlanAsync(tx);
    } catch (e) {
      // the migration is redone and saved again on the next start-up, but until then it is not stored
      this.logging.trackSilentProblem('PendingMigrationSaveFailed', errorReportFields(e));
    }
  }

  // Repair fitnessState.weight_kg for users hit by Bug A (edit-weigh-in wiped
  // the field) or Bug B (coach slot fill never propagated it). Runs on every
  // init after all singletons are loaded; piggybacks on the existing
  // flushPendingMigrationSavesAsync flush. See #3286.
  //
  // Swallows errors for the same reason flushPendingMigrationSavesAsync does:
  // recovery is cleanup, not a correctness precondition, so a transient load
  // failure here must not block login. The next session retries.
  private async recoverMissingFitnessWeightAsync(): Promise<void> {
    await this.database.withDocWatcherAsync(async watcher => {
      try {
        const intakeFormState = await this.loadIntakeFromStateAsync(watcher);
        const fitnessPlan = await this.loadPlanAsync(watcher);
        if (recoverMissingFitnessWeight(this.fitnessState, intakeFormState, fitnessPlan)) {
          this.hasPendingMigrationSaves = true;
          console.log(`Db:user: recovered fitnessState.weight_kg=${this.fitnessState.weight_kg} from intakeFormState/startPoint (#3286)`);
        }
      } catch (e) {
        // the repair is tried again on the next start-up, but until then the weight stays missing
        this.logging.trackSilentProblem('FitnessWeightRecoveryLoadFailed', errorReportFields(e));
      }
    });
  }

  // this will be called by the app to manually control initialization sequence.
  //
  // #4809: queued. Nothing stopped two start-ups from overlapping — `logonChangedAsync`
  // fires `void this.postLoginAsync(user)` without waiting for it, and the sign-in retry
  // re-emits on purpose to run the cold start again once the sync server can be reached. A
  // second start-up that began while the first was still loading used to hand out its own
  // database immediately, documents and all still missing. It now waits for the first
  // instead. A queue and not a "drop the second one" guard: that second run has a reason to
  // exist, it just has to happen afterwards.
  async initializeAsync(user: UserAuth) {
    return this.initMutex.exclusiveCall(() => this.initializeInnerAsync(user));
  }

  private async initializeInnerAsync(user: UserAuth) {
    // #6448: before anything opens, so nothing that read from the copy is alive when it goes
    await this.deletePendingImpersonationCopyAsync(user.uid);
    const database = this.dbProvider.createDatabaseWrapper();

    // df-92f044df97ec: everything from here to the hand-over is inside the try, because a
    // throw anywhere in it abandons this wrapper — see `closeAbandonedStartupDatabaseAsync`.
    let loaded: LoadedUserSingletons;
    try {
      this.registerQueries(database);

      const managingClient = this.getCoachManagingClient();
      // #4192: decide whether to open a live manage session or a read-only client
      // snapshot; when snapshotting, pass NO managedClientUid so no client-DB
      // remote sync is attempted (see routeInitialImpersonationDb for the why).
      const clientView = decideImpersonatedClientView(managingClient, this.isImpersonating());
      await database.initAsUserDatabaseAsync(
        user, UserDb.singletonIds, clientView.useSnapshot ? undefined : managingClient?.clientUid,
      );

      // routed before the documents are loaded, so they load from the right temporary
      // database — and against `database` rather than the one being served, because this
      // start-up has not handed its own out yet (#4809)
      this.routeInitialImpersonationDb(database, clientView, managingClient);

      loaded = await this.initSingletonsAsync(database);
    } catch (error) {
      await this.closeAbandonedStartupDatabaseAsync(database, AbandonedStartup.Cold);
      // The original failure travels on untouched. It is what routes the boot to
      // `interceptDatabaseError` and then to recovery or the fatal-error screen, exactly as
      // before this cleanup existed, and it is the only record of why the start-up failed.
      throw error;
    }
    // #4809: everything becomes current here, in one step, and not before — see
    // `adoptLoadedDatabase`
    this.supersedePreviousDatabase(this.adoptLoadedDatabase(database, loaded));
    await this.finishSingletonInitAsync();
  }

  /**
   * Close the wrapper a start-up is abandoning because it failed before handing it out.
   *
   * Why it has to happen here (df-92f044df97ec). `adoptLoadedDatabase` is the one place
   * `this.database` is ever assigned, and a start-up that throws before it never gets there.
   * `this.database` therefore still holds whatever came before — nothing at all on a fresh
   * page — so a later `userDb.shutdownAsync()` closes that instead, and this wrapper stays in
   * `dbProvider.activeDatabases` for the life of the page with its local PouchDB open, its
   * sync running and `isUserDb()` true. Every sweep that loops that list then acts on it: it
   * can write `databaseStates['user']` over the live wrapper's key (the `#4506` shape), and
   * `attemptGracefulRecoveryAsync` reopens its local handle and restarts its sync. Meanwhile
   * a second start-up in the same page — the sign-in retry re-emits on focus, on an online
   * event, and on the splash Retry button — opens another wrapper on the same local store.
   *
   * **Bounded, and that bound is the point.** `shutdownAsync` waits on the wrapper's
   * read-write lock, which waits for its in-flight readers — and this wrapper has a live
   * sync and a fire-and-forget `assureInitialSyncAsync` still running against a local store
   * the start-up has just failed on, so a reader that never releases is the case to design
   * for. An unbounded wait here would stop `interceptDatabaseError` and `fatalError` from
   * ever running and freeze the splash, which is worse than the leak. A close that gives up
   * has still done the urgent half: `shutdownAsync` cancels the sync and sets the read-write
   * lock's shutting-down flag synchronously, before its first await. Only the raw handles
   * stay open until the wedged operation settles.
   *
   * **What that synchronous flag costs, which is the half of this the finding entry was
   * right to ask about.** Flipping it takes the wrapper out of `activeDatabases` straight
   * away — before the rethrown error has reached `interceptDatabaseError` — so it no longer
   * takes part in `attemptGracefulRecoveryAsync`'s sweep (`recreateLocalDbAsync` returns
   * early with `true`, logging `GracefulRecoverySkippedForShuttingDownDb`, `#4506`). That is
   * what makes the close safe, and it is also a real loss: this wrapper's recreation
   * answering `false` used to be what turned the sweep false and drove
   * `recoverFromDatabaseError`, an immediate page reload. On a first cold boot there is no
   * other user wrapper, so if the ONLY store that fails recreation is this one, the sweep now
   * answers `true` from the other databases, the page is reported repaired and no reload
   * happens. The boot has already returned, so the splash watchdog's slower ladder handles it
   * instead of an immediate reload. Accepted deliberately: keeping the wrapper alive long
   * enough to vote would mean ordering this close after a recovery `app.component.ts` starts,
   * and nothing pins that ordering. `design-docs/boot-sequence.md` carries the reasoning.
   *
   * **What does NOT reach this catch**, because the shape of the leak invites the wrong guess:
   * a local store that goes silent during the document load (`#4401`) is intercepted inside
   * the wrapper and never arrives here. The batch read swallows it and falls back, and the
   * per-singleton fallback goes through `readSingletonThroughRecoveryAsync`, which calls
   * `interceptDatabaseError` itself and answers with a default rather than rethrowing — so
   * recovery runs there with this wrapper still live and still in the sweep. What reaches
   * this catch is an error thrown past those helpers, or one bound for `fatalError`.
   *
   * Always resolves, so it can never replace the failure that caused it — that error is the
   * only record of why the start-up failed at all. A close that fails or gives up is reported
   * on its own instead of being swallowed.
   */
  private async closeAbandonedStartupDatabaseAsync(
    database: DatabaseWrapper, startUp: AbandonedStartup,
  ): Promise<void> {
    try {
      await timeout(ABANDONED_STARTUP_CLOSE_TIMEOUT_MS, database.shutdownAsync());
    } catch (closeError) {
      // Two different things land here and they need different follow-ups, so say which one
      // happened — the same split `runBoundedTeardownStepsAsync` reports (`#4762`). A timeout
      // means the local store is wedged; a throw means the close itself failed.
      this.logging.trackSilentProblem(ABANDONED_STARTUP_CLOSE_PROBLEM, {
        ...errorReportFields(closeError),
        start_up: startUp,
        gave_up_reason: closeError instanceof TimeoutError ? 'timeout' : 'close_threw',
        timeout_ms: ABANDONED_STARTUP_CLOSE_TIMEOUT_MS,
      });
    }
  }

  /**
   * The two steps that close out singleton init, shared by the cold and the warm
   * path because both run exactly the same tail.
   *
   * #4401: each is bracketed as its own boot phase. Both can re-read through the
   * pass-through remote, and until now the whole surrounding region reported as
   * the single boot_stage `cold:userDb.init` — so a boot wedged in either one was
   * indistinguishable from a boot wedged anywhere else in ~80 lines. An open
   * bracket is what `bootTimings.currentPhase()` reports as `boot_step`.
   *
   * Both go through `measureAsync`, which closes the bracket however the call
   * ends. The phase brackets in app.component.ts deliberately do NOT close on a
   * throw (a throw there routes to a fatal-error path that emits no timing at
   * all, so a missing end is unobservable). These two are different: a throw
   * here is caught further up and the boot continues, so an unclosed bracket
   * would mis-name the step for the rest of that boot.
   */
  private async finishSingletonInitAsync(): Promise<void> {
    // #6248: both steps below can SAVE settings and fitness state. If any singleton holds only
    // an empty default, the cold start is about to hold the loading screen and reload, and a
    // save now would write defaults over the user's real documents. Skip; the reload redoes them.
    if (this.unreadBootSingletonIds.length > 0) return;
    await bootTimings.measureAsync(
      BootPhase.RecoverFitnessWeight, () => this.recoverMissingFitnessWeightAsync());
    await bootTimings.measureAsync(
      BootPhase.FlushMigrationSaves, () => this.flushPendingMigrationSavesAsync());
  }

  // warm resume: open local PouchDB only, load singletons, no network.
  // #4809: queued and handed out in one step, exactly like `initializeAsync` — see there.
  async initializeLocalOnlyAsync(user: UserAuth) {
    return this.initMutex.exclusiveCall(() => this.initializeLocalOnlyInnerAsync(user));
  }

  private async initializeLocalOnlyInnerAsync(user: UserAuth) {
    // #6448: same as initializeInnerAsync
    await this.deletePendingImpersonationCopyAsync(user.uid);
    const database = this.dbProvider.createDatabaseWrapper();

    // #4809: close this wrapper ourselves if the start-up throws. Nothing else can any more —
    // `this.database` still holds whatever came before (nothing at all on a fresh page), so
    // the warm-resume fallback's `userDb.shutdownAsync()` would close that instead and leave
    // this one open on the same local store, with its sync running and `isUserDb()` true. The
    // fallback then opens a second wrapper on the same store. Before the hand-out moved, the
    // fallback closed this wrapper because it had already been assigned.
    //
    // df-92f044df97ec: the try now starts here rather than at the document load, because
    // opening the local store leaks the identical wrapper a few lines earlier, and the close
    // is the shared bounded one — see `closeAbandonedStartupDatabaseAsync` for why it is
    // bounded and what a close that gives up has still achieved.
    let loaded: LoadedUserSingletons;
    try {
      this.registerQueries(database);

      const managingClient = this.getCoachManagingClient();
      // #4192: same snapshot-vs-live routing as initializeAsync (see there).
      const clientView = decideImpersonatedClientView(managingClient, this.isImpersonating());
      await database.initAsUserDatabaseLocalOnlyAsync(
        user, UserDb.singletonIds, clientView.useSnapshot ? undefined : managingClient?.clientUid,
      );

      // same as initializeAsync: routed before the load, against this start-up's own database
      this.routeInitialImpersonationDb(database, clientView, managingClient);

      loaded = await this.initSingletonsAsync(database);
    } catch (error) {
      // The close is best-effort and must never replace the failure that caused it: this is
      // the double-failure case somebody would be debugging, and the load's error is the only
      // record of why the warm resume failed at all.
      await this.closeAbandonedStartupDatabaseAsync(database, AbandonedStartup.Warm);
      throw error;
    }
    this.supersedePreviousDatabase(this.adoptLoadedDatabase(database, loaded));
    await this.finishSingletonInitAsync();
  }

  // warm resume Phase B: connect remote and start sync
  async connectRemoteAsync() {
    await this.database?.connectRemoteAndSyncAsync();
  }

  // #4809: shutdown joins the same queue as the two start-ups. Signing out used to be able
  // to close the OLD database while a start-up was still loading a new one — and that start-up
  // would then hand its database out, still syncing, after the sign-out, with nothing left to
  // close it.
  //
  // The two callers wait differently, and only one of them is bounded.
  //
  // Signing out is bounded: `runBoundedTeardownStepsAsync` gives every teardown step
  // SIGNOUT_TEARDOWN_STEP_TIMEOUT_MS, so a start-up still holding the queue just means the
  // step is given up, and the login page wipes local data moments later regardless.
  //
  // The warm-resume fallback (the catch in `postLoginWarmResumeAsync`) is NOT bounded, and
  // deliberately so. Bounding it would gain nothing: the very next thing that fallback does is
  // start a cold start-up, which joins this same queue behind the same holder. Waiting is also
  // the right behavior — the alternative is tearing down a database while a start-up is
  // mid-load, which is what this queue exists to stop. A start-up that never finishes at all
  // is the splash watchdog's case, not this one's.
  async shutdownAsync() {
    this.shutdownsQueuedOnInitMutex++;
    return this.initMutex.exclusiveCall(() => {
      this.shutdownsQueuedOnInitMutex--;
      return this.shutdownInnerAsync();
    });
  }

  /**
   * Where a sign-out shutdown of this database is, for the gave-up report (df-5c749cefc673).
   * Still queued behind a start-up holding `initMutex` is reported as its own phase, because
   * the wrapper cannot see that wait; past it, the wrapper says which of its waits it is in.
   */
  describeShutdown(): DatabaseShutdownDescription {
    if (this.shutdownsQueuedOnInitMutex > 0) {
      return { shutdown_phase: DatabaseShutdownPhase.QueuedInitMutex };
    }
    return this.database?.describeShutdown() ?? { shutdown_phase: DatabaseShutdownPhase.NotStarted };
  }

  private async shutdownInnerAsync() {
    if (this.scheduleWatcher) {
      this.scheduleWatcher.destroy();
      this.scheduleWatcher = null;
    }
    // #6280, #6828: the held days belong to the database being shut down
    this.pastBackfillHold?.stop();
    this.pastBackfillHold = null;
    this.todayResaveHold?.stop();
    this.todayResaveHold = null;
    // #4249: no reactive timer may outlive the database it belongs to
    this.dayTaskStatus.destroyPulledDocListener();
    this.dayTaskStatus.resetReactiveDayStatusState();
    if (this.database) {
      await this.database.shutdownAsync();
      // don't null this.database — in-flight async callers would crash with
      // "Cannot read properties of null". The DatabaseWrapper already nulls its
      // internal localDb/remoteDb, so post-shutdown operations safely no-op.
    }
  }

  isInitialized() {
    return this.database != null && this.database.getReadDb() != null;
  }

  /**
   * Every document a coach-invite claim writes into, in the order the claim writes
   * them. Named separately from `singletonIds` on purpose: that list is what
   * start-up pre-creates slots for, and it does NOT contain the coach-relationship
   * document — which is the single most important one the claim writes, because it
   * is what tells the app who the client's coach is.
   */
  private static readonly coachClaimSingletonIds = [
    intakeFormId, currentPlanId, fitnessStateId, declaredStateId, immutableStateId,
    scheduleId, coachRelAsClientId,
  ];

  /**
   * Are all the documents a coach-invite claim writes into loaded and ready? (#4801)
   *
   * `isInitialized()` above is NOT this question and must not be used for it: it only
   * asks whether there is a database handle, which is true throughout the window where
   * every document is still unloaded. That gap is what let a claim copy a coach's whole
   * training program into documents that did not exist yet, silently.
   */
  areCoachClaimDocumentsLoaded(): boolean {
    if (!this.isInitialized()) return false;
    return UserDb.coachClaimSingletonIds.every(id => this.database.isSingletonLoaded(id));
  }

  // True iff the current user has at least one claimed coach client. Used as a UX
  // fast-path in `saveAsync` to skip the auto-publish-to-coach-channel cloud function
  // call when the user clearly isn't a coach with active clients. Authoritative validation
  // of the same condition happens server-side in `publishExerciseToCoachChannel_impl` —
  // a sync-lag mismatch between this and the server is expected and the server's rejection
  // is the correct outcome (logged + dropped, not surfaced to the user).
  async hasClaimedClientsAsync(): Promise<boolean> {
    if (!this.database) return false;
    // getByIdHeadAsync returns decoded docs (already unwrapped from PouchDB rows).
    // It THROWS DatabaseUnavailableError rather than answering with an empty
    // list when the database is not available, so there is nothing to guard
    // against here — callers of this method must be ready for that throw.
    const slots = (await this.database.withDocWatcherAsync(w =>
      this.database.getByIdHeadAsync('coach-client/', w))) as CoachClientSlot[];
    return slots.some((s) => s.status === CoachClientStatus.Claimed);
  }

  getEmptyDebugState(): DebugState {
    const dbg = {} as DebugState;
    return dbg;
  }

  getEmptyImmutableState(): ImmutableState {
    const imm = {} as ImmutableState;
    return imm;
  }

  getEmptyCustomMeasures() {
    const customMeasures = {
      names: [],
    };

    return customMeasures;
  }

  async loadIntakeFromStateAsync(watcher: DocWatcher): Promise<IntakeFormState> {
    return this.database.getSingleton(intakeFormId, watcher);
  }

  async saveIntakeFromStateAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, intakeFormId);
  }

  clearIntakeFormState(intakeFormState: IntakeFormState) {
    deepOverwriteCouchSafe(intakeFormState, this.getEmptyIntakeFormState());
  }

  getEmptyProfileState(): ProfileState {
    let doc = {} as ProfileState;
    doc._id = profileStateId;
    doc.lastTodoActions = [];
    return doc;
  }

  /**
   * The person's profile, or an empty one when they have none yet (a new user).
   *
   * Any other failure is thrown, never answered with the empty profile (df-9241e82523c2):
   * "could not look" is not "there is nothing there", and a caller that saved the stand-in
   * would write it over the real profile. Each caller decides what a failed read means.
   */
  async loadProfileStateAsync(watcher: DocWatcher): Promise<ProfileState> {
    try {
      return await this.database.getRecordAsync(profileStateId, watcher);
    } catch (e) {
      if (!isPouchNotFound(e)) throw e;
      return this.getEmptyProfileState();
    }
  }

  async saveProfileStateAsync(doc: ProfileState, transaction: TransactionId): Promise<void> {
    // awaited rather than returned: saveRecordAsync now answers with what became of the
    // write (df-068808a2796e), and that answer is for the delete paths, not for here.
    await this.database.saveRecordAsync(doc, transaction);
  }


  getEmptyIntakeFormState(): IntakeFormState {
    const intakeFormState = {} as IntakeFormState;
    intakeFormState.declaredStrength = {
      slots: [
        /*        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_bench_press", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
      */
      ],
    };

    intakeFormState.exerciseStats = {};
    intakeFormState.trainingDays = [];
    intakeFormState.bfData = {
      method: null,
      chest_mm: [],
      suprailiac_mm: [],
      umbilical_mm: [],
      thigh_mm: [],
      tricep_mm: [],
    };
    return intakeFormState;
  }

  getEmptySettings(): Settings {
    const settings: Settings = {
      _id: settingsId,
      clockHourMode: ClockHourMode.Automatic,
      nutritionLabelStyle: undefined,
      foodValuesDisplayMode: undefined,
      cardAnimations: true,
      testMode: false,
      progressionTesting: false,
      progressionTesting_Slot: '',
      progressionTesting_Sets: undefined,
      progressionTesting_Level: TrainingLevel.Intermediate,
      progressionTesting_otherLevel: TrainingLevel.Intermediate,
      progressionTesting_Intensity: IntensityLevel.DUPheavy,
      progressionTesting_SlotIntensities: '',
      debugFoodSearch: false,
      cachedRole: AuthRole.Unknown,
      cachedRoleExpiry_utc: 'never',
      unitsPrefsJSON: '',
      hintsSeenJSON: '',
      helpSeenJSON: '',
      debugShowAllHints: false,
      debugShowAllTutorials: false,
      locale: {},
      currentWOSolverTestId: '',
      usedCommunityBefore: false,
      hasLoggedFoodEver: false,
      showReportBugFAB2: false,
      barcodeCameraId: '',
      barcodeCameraZoom: null,
      barcodeCameraFunctional: false,
      showDayStartEvents: true,
      showDayEndEvents: true,
      migrated: false,
    };
    return settings;
  }

  async loadSettingsAsync(watcher: DocWatcher): Promise<Settings> {
    return this.database.getSingleton(settingsId, watcher);
  }

  async saveSettingsAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, settingsId);
  }

  async loadDebugStateAsync(watcher: DocWatcher): Promise<DebugState> {
    return this.database.getSingleton(debugStateId, watcher);
  }

  async saveDebugStateAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, debugStateId);
  }

  async loadImmutableStateAsync(watcher: DocWatcher): Promise<ImmutableState> {
    return this.database.getSingleton(immutableStateId, watcher);
  }

  async saveImmutableStateAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, immutableStateId);
  }

  async saveUserMeasuresAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, customMeasuresId);
  }

  getEmptyDeclaredState(): DeclaredState {
    const life = {} as DeclaredState;

    life.declaredStrength = {
      slots: [
        /*        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_bench_press", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
        { idSlot: "slot_squat", trainingLevel: TrainingLevel.unknown },
      */
      ],
    };

    return life;
  }

  // never wait for the config here: on a warm restart it loads only after the server login, and
  // every page that loads the declared state would wait with it (#6775, boot-sequence.md)
  onDeclaredStateChange(declaredState: DeclaredState): void {
    this.nutritionTracking = declaredState.nutritionTracking !== NutritionTrackingType.None;
    this.nutritionTargets = declaredState.nutritionCoaching !== NutritionCoachingType.None;
    this.macrosFlexible = declaredState.macroTargets === MacroTargetsType.Flexible;
    this.calorieCycling = usesCalorieCycling(declaredState);
  }
  async loadDeclaredStateAsync(watcher: DocWatcher): Promise<DeclaredState> {
    const declaredState = this.database.getSingleton(declaredStateId, watcher);
    // This should patch old versions of the declared state
    if (declaredState['advancedMethods'] !== undefined) {
      declaredState.advancedMethodsMyoReps = declaredState['advancedMethods'];
      delete declaredState['advancedMethods'];
    }
    // calculate sleepHours from wakeupTime and bedTime for old users
    if (declaredState.sleepHours === undefined && declaredState.wakeupTime && declaredState.bedTime) {
      const wakeupHours = isoTimeToHours(declaredState.wakeupTime);
      const bedHours = isoTimeToHours(declaredState.bedTime);
      const rawHours = 24 - (bedHours - wakeupHours);
      declaredState.sleepHours = Math.round(rawHours * 2) / 2;
    }
    this.onDeclaredStateChange(declaredState);
    return declaredState;
  }

  async saveDeclaredStateAsync(transaction: TransactionId) {
    const declaredState = this.database.withDocWatcher(watcher =>
      this.database.getSingleton(declaredStateId, watcher));
    this.onDeclaredStateChange(declaredState);
    return this.database.saveSingletonAsync(transaction, declaredStateId);
  }

  getEmptyFitnessState(): FitnessState {
    const fitness: FitnessState = {
      _id: fitnessStateId,
      weight_kg: undefined,
      allEquipment: {},
      exerciseStats: {},
      bfData: {
        method: null,
        chest_mm: [],
        suprailiac_mm: [],
        umbilical_mm: [],
        thigh_mm: [],
        tricep_mm: [],
      }
    }
    return fitness;
  }

  async loadFitnessStateAsync(watcher: DocWatcher): Promise<FitnessState> {
    return this.database.getSingleton(fitnessStateId, watcher);
  }

  // `committedState` plays exactly the same part here as `committedPlan` does in
  // savePlanAsync — the object a `CouchDocDraft` caller's `commitDraft()` just wrote the
  // user's edits into. The Edit Workouts page saves the plan and the fitness state on one
  // press of Save, so leaving this one out would still lose half of what the user typed.
  async saveFitnessStateAsync(transaction: TransactionId, committedState?: FitnessState) {
    // only read at all when there is something to compare against, so a caller that
    // tracks no committed object costs exactly what it used to (df-224ad165d843)
    if (committedState != null) {
      const liveState = this.database.withDocWatcher(watcher =>
        this.database.getSingleton(fitnessStateId, watcher)) as FitnessState;
      if (carryCommittedEditsOntoSaveTarget(liveState, committedState)) {
        this.logging.trackSilentProblem('fitness-state.save-target-replaced', { transaction });
      }
    }
    return this.database.saveSingletonAsync(transaction, fitnessStateId);
  }

  loadCoachRelAsClient(): CoachRelationship {
    return this.database.withDocWatcher(watcher =>
      this.database.getSingleton(coachRelAsClientId, watcher));
  }

  async saveCoachRelAsClientAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, coachRelAsClientId);
  }

  // Append an exercise id to the imported-global-exercises list; idempotent. The
  // singleton is read via the identity-map cache (mutation lands on the live ref) and
  // saved on the existing transaction. The DocWatcher in `coach-exercise-sync.service`
  // reacts to the change by restarting Replication B with the updated id list.
  async addImportedGlobalExerciseIdAsync(exerciseId: string, transaction: TransactionId): Promise<void> {
    // the save stays inside the body so the watcher is released at the same point it
    // always was — after the write, not between the read and the write.
    await this.database.withDocWatcherAsync(async watcher => {
      // no "if (!doc) return" guard any more: since #4801 `getSingleton` REFUSES with a
      // DatabaseUnavailableError when start-up has not loaded the document, rather than
      // answering undefined. Returning quietly here would have hidden exactly the kind
      // of lost write that ticket was about.
      const doc = this.database.getSingleton(importedGlobalExercisesIdsId, watcher) as ImportedGlobalExercisesIds;
      // check membership BEFORE assigning, so the no-op already-tracked case doesn't
      // mutate the singleton (would be saved-as-equal but still touches identity-map).
      if (doc.ids?.includes(exerciseId)) return;
      doc.ids = doc.ids ?? [];
      doc.ids.push(exerciseId);
      await this.database.saveSingletonAsync(transaction, importedGlobalExercisesIdsId);
    });
  }

  /**
   * The coach's own client roster, read from whichever database is currently open.
   *
   * During a manage session that is the CLIENT's database, which cannot hold this
   * document — and the read answers an empty roster rather than failing, so nothing
   * can tell that apart from a coach with no clients. Reported here rather than left
   * to each caller, because no caller is able to notice it. The returned value is
   * deliberately unchanged; see `design-docs/coach-mode.md`, "Reading the coach's own
   * roster", for why, and for what stops any of today's screens reaching this.
   *
   * When you need the roster during a manage session, use
   * `loadCoachRosterFromOwnDbAsync` below — it opens the coach's own database.
   */
  loadCoachRelAsCoach(): CoachRelationship {
    if (this.getCoachManagingClient()) {
      // name the screen that asked, which is the question a debugger arrives with.
      // Parsing rather than slicing the path is what makes that both correct and
      // safe: a page's parameters live INSIDE its own segment (`page:uid~<id>`), so
      // cutting on `/` would keep a client id, and the current page is the LAST
      // segment because the ones before it are the back-trail.
      const pages = parseUrlToPages(window.location?.pathname ?? '');
      this.logging.trackSilentProblem('CoachRosterReadFromManagedClientDb', {
        route: pages[pages.length - 1]?.segment ?? '',
      });
    }
    return this.database.withDocWatcher(watcher =>
      this.database.getSingleton(coachRelAsCoachId, watcher));
  }

  async saveCoachRelAsCoachAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, coachRelAsCoachId);
  }

  /**
   * Read the coach's client roster out of the coach's OWN local database, even when the
   * database the app is currently working against belongs to somebody else.
   *
   * This exists because `loadCoachRelAsCoach` above reads whichever database is open, which
   * is the wrong one during a manage session. `design-docs/coach-mode.md` ("Naming the
   * managed client", #4902) has the full explanation.
   *
   * Answers `null` when the roster is genuinely not there. Every other failure — including
   * not knowing whose roster to read — is thrown, so a broken read can never be mistaken for
   * a coach with no clients. That confusion is the bug this method exists to undo, and
   * answering `null` for anything else would quietly recreate it.
   *
   * `signedInUid` is passed in because this service has no `AuthService` (it would be a
   * circular dependency). The impersonation override is applied here rather than by the
   * caller: when an admin is impersonating a coach, the signed-in account is the ADMIN, while
   * the coach's data sits in `userdb-fr_<coachUid>` as a downloaded snapshot — and this class
   * is the one that knows that.
   */
  async loadCoachRosterFromOwnDbAsync(signedInUid: string): Promise<CoachRelationship | null> {
    const impersonatedUid = this.getImpersonationUserAuth()?.uid || undefined;
    const ownerUid = impersonatedUid || signedInUid;
    if (!ownerUid) {
      throw new Error('loadCoachRosterFromOwnDbAsync: no signed-in or impersonated user, so there is no roster to read');
    }

    // PouchDB has no "open only if it already exists", so this CREATES an empty local database
    // when the coach's own one is absent. By the time a manage session exists, the coach has
    // booted as themselves on this device at least once, so it is there; the only way it is
    // not is localStorage outliving an IndexedDB wipe, and then the cost is a few empty
    // kilobytes and the caller's ordinary fallback. `createOrLoadDatabase` rather than a bare
    // `new PouchDB` so the native adapter options and the corruption reporter are wired.
    // the name follows a local-store reset for the coach's own database, as `DatabaseWrapper`
    // does when it opens it, and stays plain for an impersonation copy (df-a5263ec09237).
    const ownDb = this.dbProvider.createOrLoadDatabase(
      coachOwnRosterDbName(browserLocalStorageOrNull(), impersonatedUid, signedInUid));

    // NOTE: do not close this handle. PouchDB's IndexedDB adapter caches one connection per
    // database NAME and hands it to every instance opened under that name, and closing any
    // one instance closes that shared connection and drops it from the cache. Every other
    // live handle to the same database keeps its now-dead reference and fails on its next
    // read. Nothing collides on today's only call path, but this method is public and the
    // page is about to reload anyway, so leaving the handle to be collected is the safe
    // direction — a leaked handle costs nothing, a closed shared connection breaks the app.
    try {
      return await ownDb.get(coachRelAsCoachId) as CoachRelationship;
    } catch (err) {
      if (isPouchNotFound(err)) return null;
      throw err;
    }
  }

  getEmptyPlan(): FitnessPlan {
    const plan = {} as FitnessPlan;
    plan.welcomePhase = WelcomePhase.Intro;
    plan.subSlots = {};
    plan.workouts = [];
    plan.trainingLevels = {
      $bench_press: TrainingLevel.Novice,
      $chin_up: TrainingLevel.Novice,
      $ohp: TrainingLevel.Novice,
      $rdl: TrainingLevel.Novice,
      $squat: TrainingLevel.Novice,
    };

    return plan;
  }

  async loadPlanAsync(watcher: DocWatcher): Promise<FitnessPlan> {
    const plan = (await this.database.getSingleton(currentPlanId, watcher)) as FitnessPlan;

    // a runtime read, so it acts on the database and fitness state currently being served
    let needsSaving = await this.backCompatFitnessPlan(this.database, plan, this.fitnessState);

    // give the as-built copy the same format repairs the live program gets below, so the
    // rebuild warning does not read an old account as customised (df-dadb3d927571). It runs
    // before the #4587 repair, which may restore the program from this copy
    if (normalizeProgramBackupAtLoad(plan.generatedPlanBackup)) {
      needsSaving = true;
    }

    // bring the live program into today's format: old slot ids, day entries pointing at
    // sub-slots the plan no longer holds, repeated entries, sub-slots no day references, and
    // missing intensity tables. The as-built copy above runs the SAME function, which is what
    // keeps the two in step (df-0c02f36b5c60) — add a new load-time repair there, not here
    const repaired = repairProgramFormatAtLoad(plan);
    if (repaired.changed) {
      needsSaving = true;
    }
    // the copy reports nothing; the live program does, because a day losing an exercise is
    // still wrong for the user even once the dangling reference is gone
    reportMissingSubSlotsHealedAtLoad(repaired.healedMissing, this.logging);

    // #4587: a plan that was already built, but now holds no exercises at all, is damage.
    // The app would otherwise carry on treating it as finished and put blank workouts on
    // the user's calendar for ever. Try to put the exercises back from something the app
    // already stored; if nothing can, say so and ask for a rebuild. This runs after the
    // heals above so it sees the plan in its final shape, and it is skipped entirely for
    // every healthy plan, which is the overwhelmingly common case on this boot path.
    if (planIsBuiltButHasNoExercises(plan, this.workoutsPerWeekOrUnknown())) {
      if (await this.repairPlanWithNoExercisesAsync(plan)) {
        needsSaving = true;
      }
    }

    // a second pass, for the sub-slots the #4587 recovery just put back: they come from a
    // stored copy that may predate intensities, and they arrive after the repair above ran
    if (fillMissingIntensities(plan.subSlots)) {
      needsSaving = true;
    }

    if (needsSaving) {
      await this.savePlanAsync(DatabaseWrapper.getTransactionId());
    }

    return plan;
  }

  /**
   * #4587: put the training structure back into a built plan that has lost every exercise,
   * or — when nothing can be recovered — leave the plan alone, report it, and ask for a
   * rebuild so the user is not left with blank workouts and no way out.
   *
   * Returns whether the plan was changed and so needs saving.
   *
   * The sources are tried together rather than in a fixed order, and the NEWEST usable one
   * wins: a stored plan backup written after the user's last workout knows about edits the
   * workout logs cannot, and a workout logged after the last backup knows about exercises
   * that backup predates. The plan's own generated backup carries no date and describes the
   * last time the programme was GENERATED, so it only stands in when nothing dated is
   * usable. `fitness-plan-recovery.ts` holds those rules, unit-tested on plain objects.
   */
  private async repairPlanWithNoExercisesAsync(plan: FitnessPlan): Promise<boolean> {
    // Every plan load reaches this method while the plan stays damaged, and many pages
    // load the plan. Without this the two view reads below, and the report at the end,
    // would repeat on every one of them — an unbounded stream of identical Sentry events
    // is how a real signal becomes unfindable. Attempting once per app session still
    // retries after a restart, which is what matters: the copy this needs may only arrive
    // by sync, and a restart is the point at which it is worth looking again.
    if (this.planRepairAttempted) {
      return false;
    }
    this.planRepairAttempted = true;

    const candidates: (PlanRecoveryCandidate | undefined)[] = [];
    try {
      candidates.push(await this.findNewestUsablePlanBackupAsync());
      candidates.push(rebuildTrainingStructureFromWorkoutLogs(await this.loadRecentCompletedWorkoutsAsync()));
    } catch (err) {
      // A read that could not RUN is not the same fact as "there is nothing stored", and
      // acting as if it were is how this repair would do real harm: it would fall through
      // to the undated last resort, restore a plan from the last time it was GENERATED,
      // and save it — after which the newer copies it never managed to read are out of
      // reach for good, because the plan is no longer damaged and this never runs again.
      // So a failed read abandons the whole attempt and leaves the plan untouched. The
      // next app start looks again, which is the right answer for a database that was
      // merely recovering at that moment.
      if (isDatabaseTornDown(err)) {
        // our own sign-out wipe is not a fault, and `trackSilentProblem` writes a
        // console.error the E2E browser-error gate fails on
        console.log(`Plan repair skipped — our own teardown (${err.reason})`);
      } else if (!this.planRepairReadFailureReported) {
        this.planRepairReadFailureReported = true;
        if (isDatabaseUnavailable(err)) {
          this.logging.trackSilentProblem('plan.recovery-read-unavailable', { reason: err.reason });
        } else {
          this.logging.trackSilentProblem('plan.recovery-read-failed', errorReportFields(err));
        }
      }
      // let the next session try again rather than burning the one attempt on a read that
      // never happened
      this.planRepairAttempted = false;
      return false;
    }
    candidates.push(planBackupAsRecoveryCandidate(
      plan.generatedPlanBackup, undefined, PlanRecoverySource.GeneratedPlanBackup));

    const best = pickBestPlanRecoveryCandidate(candidates);
    // restoreTrainingProgram reads and writes both of these, so a repair cannot run before
    // they exist. fitnessState is loaded before the plan during init, and deepNormalize
    // fills in an absent subSlots from the default plan, so this is a guard against an
    // order nothing currently produces rather than a case seen in the wild — but a repair
    // that threw here would take the whole plan load down with it.
    if (best && this.fitnessState && plan.subSlots) {
      restoreTrainingProgram(plan, this.fitnessState, best, this.debug.getNowLocal());
      // restoreTrainingProgram moves whatever the damaged plan still held into
      // fitnessState.oldSubslots before replacing the dictionary, so fitnessState now
      // carries the only remaining copy of those entries. Saving the plan alone would
      // drop them — the exercises the new gcOrphanSubslots guard has just finished
      // protecting. Every other caller of the switching helpers saves both for the same
      // reason; this one has to as well, and the plan's own save follows in loadPlanAsync.
      await this.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
      // A rebuild from the workout logs can only cover days the user actually trained,
      // and it has no way to know how many days the plan is meant to have — so ask for a
      // plan rebuild as well, rather than presenting a possibly short plan as finished.
      if (best.source === PlanRecoverySource.WorkoutLogs) {
        plan.needsReplan = true;
      }
      // a repair that has already happened is information, not an unresolved problem
      // (design-docs/telemetry.md). The counts say how much came back, so a reader can
      // tell a full restore from a partial one without the plan itself in the payload.
      this.logging.trackSilentInfo('plan.recovered-lost-exercises', {
        source: best.source,
        copiedFrom_utc: best.timestampUtc,
        workouts: plan.workouts.length,
        subSlots: Object.keys(plan.subSlots).length,
        needsReplan: !!plan.needsReplan,
      });
      return true;
    }

    // nothing to recover from. Leave the plan as it is — the exercises are already gone,
    // and the heals above no longer delete what little may still be there — but stop the
    // app pretending the plan is usable.
    const alreadyAsked = plan.needsReplan;
    plan.needsReplan = true;
    this.logging.trackSilentProblem('plan.built-plan-has-no-exercises', {
      workouts: plan.workouts?.length ?? 0,
      subSlots: Object.keys(plan.subSlots ?? {}).length,
    });
    return !alreadyAsked;
  }

  /**
   * #4587: the newest stored plan backup that still holds exercises.
   *
   * The app writes a full copy of the plan into a `log/<ts>-FitnessPlan` document before
   * every plan save, and the `planBackupById` view indexes those by that timestamped id.
   * Reading it newest-first and stopping at the first usable copy keeps the cost to the
   * few documents actually needed, rather than every backup the user has ever accumulated.
   *
   * Reading past the first one matters: the damaged plan was journalled too, so the newest
   * backups can themselves be empty — in the incident behind this ticket the very newest
   * one was. `PLAN_BACKUP_RECOVERY_SCAN_LIMIT` bounds how far back that search goes.
   */
  private async findNewestUsablePlanBackupAsync(): Promise<PlanRecoveryCandidate | undefined> {
    // descending swaps the roles of the two keys, so the range still runs over `log/...`
    const rows: { id: string; doc: FitnessPlan }[] = await this.database.runQueryAsync(
      'planBackupById', undefined,
      { startkey: 'log/\uffff', endkey: 'log/', descending: true, include_docs: true,
        limit: PLAN_BACKUP_RECOVERY_SCAN_LIMIT });

    for (const row of rows) {
      if (!row?.doc) continue;
      const timestampUtc = row.id.match(/log\/(.+)-FitnessPlan/)?.[1];
      const candidate = planBackupAsRecoveryCandidate(
        row.doc, timestampUtc, PlanRecoverySource.ScannedBackup);
      if (candidate) return candidate;
    }
    return undefined;
  }

  /**
   * The first plan backup journalled at or after `timeUtc` (a UTC ISO time), or undefined when
   * there is none (df-532ecfe078a4). The `planBackupById` view indexes ids of the form
   * `log/<UTC ISO>-FitnessPlan`, so a range starting at `log/<timeUtc>` reaches exactly the
   * backups taken from then on, and the first one is the plan as it stood just before the next
   * save after that time. A same-millisecond collision copy (`…-FitnessPlan_1`) is not in the
   * view; the base copy from that millisecond is, and it holds the earlier plan.
   */
  async loadFirstPlanBackupAfterAsync(timeUtc: string): Promise<FitnessPlan | undefined> {
    const rows: { id: string; doc: FitnessPlan }[] = await this.database.runQueryAsync(
      'planBackupById', undefined,
      { startkey: 'log/' + timeUtc, endkey: 'log/\uffff', include_docs: true, limit: 1 });
    return rows[0]?.doc;
  }

  /**
   * #4587: the user's most recent completed workouts, newest first, for rebuilding the
   * plan from the copies they each carry of their own exercises.
   *
   * Only completed workouts are read: a workout that was never trained carries no
   * `planSubslotBackup`, because that copy is written when the user concludes an
   * exercise's first set. The limit is generous enough to cover every day of a split
   * several times over, so the rebuild can find the newest log for each day.
   */
  private async loadRecentCompletedWorkoutsAsync(): Promise<Log_Workout[]> {
    const rows: { id: string; doc: Log_Workout }[] = await this.database.runQueryAsync(
      'completedWorkouts', undefined,
      { startkey: 'log/\uffff', endkey: 'log/', descending: true, include_docs: true,
        limit: WORKOUT_LOG_RECOVERY_SCAN_LIMIT });
    return rows.filter(row => row?.doc).map(row => row.doc);
  }

  // returns true if the plan was modified and needs saving.
  //
  // #4809: takes the database and the already-loaded fitness state rather than reading
  // `this.database` and `this.fitnessState`. It runs DURING start-up's load, before this
  // service has started serving the new database — so reading `this` here would have meant
  // a null database on the very first boot, and the PREVIOUS account's log entries and
  // weight on any later one.
  private async backCompatFitnessPlan(
    database: DatabaseWrapper, plan: FitnessPlan, loadedFitnessState: FitnessState | undefined,
  ): Promise<boolean> {
    let needsSaving = false;

    // handling of old versions of the plan that didn't contain reference point
    // (we detect this if the plan was "ready", but didn't generate a start point at that time)
    // this should only be activated for alpha user's accounts used before nutrition updates were implemented
    if (plan.startPoint === undefined && plan.ready) {
      // the fitness state is already loaded by the time the plan's updater runs (the spec
      // order guarantees it), and it IS the document this database holds — so it is used
      // directly instead of being read back out of the database a second time
      const firstLog = await database.withDocWatcherAsync(async watcher => {
        const firstLogs = await database.getByIdHeadAsync('log/', watcher, { limit: 1 });
        return firstLogs[0] as Log_Entry;
      });
      if (firstLog && loadedFitnessState && loadedFitnessState.bfData && loadedFitnessState.bfData.bf_pc) {
        plan.startPoint = {
          timestamp_utc: firstLog.time_utc,
          BW_kg: loadedFitnessState.weight_kg,
          BF_pc: loadedFitnessState.bfData.bf_pc,
        };
      }
      needsSaving = true;
    }

    // welcomePhase migrations live in shared so they are unit-testable
    // (see backCompatWelcomePhase in fitness-plan.ts, #4125)
    if (backCompatWelcomePhase(plan)) {
      needsSaving = true;
    }

    // fixup eventual old slots. Shared with the load-time repair so there is one definition
    // of this step — this method also runs for the boot migration and the old-plans list,
    // which do NOT get the rest of that repair
    if (repairObsoleteSubSlotIds(plan.subSlots)) {
      needsSaving = true;
    }

    // fixup subslots in workouts
    if (plan.workouts) {
      for (const workout of plan.workouts) {
        if (workout.subSlotIds) {
          if (workout.subSlotIds.some(subSlotId => isObsoleteSlotId(subSlotId))) {
            workout.subSlotIds = workout.subSlotIds.map(subSlotId => fixupSlotId(subSlotId));
            needsSaving = true;
            // make sure that the subSlotIds are unique
            workout.subSlotIds = uniq(workout.subSlotIds);
          }
        }
      }
    }
    return needsSaving;
  }

  // persist the current fitness plan singleton; trips the orphan-subSlot invariant canary
  // at the write boundary so any write path that hands an inconsistent plan to the save
  // surfaces a Sentry event tied to its own transaction id (#3320).
  //
  // `committedPlan` is for a caller that edits through a `CouchDocDraft`: the object its
  // `commitDraft()` just wrote the user's edits into. Pass it and the user's edits are
  // what gets saved even if the database was re-opened under the caller's feet; leave it
  // out — as every caller that mutates the live singleton directly does — and nothing
  // about this method changes. See `carryCommittedEditsOntoSaveTarget` for the whole
  // mechanism (df-224ad165d843).
  //
  // `saveContext` is optional diagnosis for the orphan canary below: which caller asked, and
  // what the workout page knows. It changes nothing about the save (df-704db86556f7).
  async savePlanAsync(transaction: TransactionId, committedPlan?: FitnessPlan, saveContext?: PlanSaveContext) {
    // #3320: orphan-subslot invariant at the write boundary — if any write path leaves
    // plan.subSlots entries unreferenced by plan.workouts, surface the leaking caller and
    // heal in place so we never persist a broken plan. Load-time GC's own heal-then-save
    // path naturally bypasses (it deletes orphans before getting here), so latent pre-#3320
    // data drains silently.
    //
    // Atomicity requirement for callers: a new exercise's dict entry (plan.subSlots[id])
    // and its workout reference (workout.subSlotIds.push(id) or equivalent) MUST land
    // before savePlanAsync is invoked — half-written state (dict entry without workout
    // reference) will be silently destroyed by this heal AND mis-reported under the
    // canary fingerprint. All existing writers (addExerciseToWorkoutAsync and
    // changeExerciseInWorkoutAsync in services/exercise-switching.ts, and removeExerciseFromWorkout in
    // pages/edit-workouts/edit-workouts-page.ts) satisfy this by populating both sides
    // before reaching the save, even when the write itself is part of an async sequence.
    // No `if (plan)` guard any more either: since #4801 the read either answers a real
    // document or refuses, so a falsy plan is no longer a case that can occur.
    const plan = this.database.withDocWatcher(watcher =>
      this.database.getSingleton(currentPlanId, watcher)) as FitnessPlan;
    // df-224ad165d843: the caller committed its edits into an object the database no
    // longer holds, so saving what the database holds would throw the user's whole
    // editing session away with nothing reporting it. Carry the edits across instead.
    // Deliberately BEFORE the invariant checks below, so every one of them judges the
    // content that is really about to be persisted rather than the content it replaced.
    if (carryCommittedEditsOntoSaveTarget(plan, committedPlan)) {
      this.logging.trackSilentProblem('plan.save-target-replaced', { transaction });
    }
    // #4587: last line of defence. Whatever route brought a built plan here with no
    // exercises, it must not be persisted as a finished plan — that is what left a customer
    // with blank workouts and no way out. The recovery attempt lives in loadPlanAsync,
    // which every read passes through; this only makes sure a plan that reached the save
    // some other way still asks for a rebuild rather than going out silently.
    if (planIsBuiltButHasNoExercises(plan, this.workoutsPerWeekOrUnknown())) {
      plan.needsReplan = true;
      this.logging.trackSilentProblem('plan.built-plan-has-no-exercises-at-save', {
        transaction,
        workouts: plan.workouts?.length ?? 0,
        subSlots: Object.keys(plan.subSlots ?? {}).length,
      });
    }

    // capture pre-heal size so the canary payload reports the count the offending writer
    // actually persisted, not the post-heal residual
    const totalSubSlotsBeforeHeal = Object.keys(plan.subSlots).length;
    const orphanIds = gcOrphanSubslots(plan);
    if (orphanIds.length > 0) {
      // df-704db86556f7: say which route left the orphan — compared against the copy the
      // database last held, plus whatever the caller told us
      const stored = this.database.rememberedDatabaseBodyOf(currentPlanId);
      this.logging.trackSilentProblem('plan.orphan-subslot-at-save', {
        transaction,
        orphanIds,
        totalSubSlots: totalSubSlotsBeforeHeal,
        totalWorkouts: plan.workouts.length,
        ...describeOrphanSubSlotsAtSave(orphanIds, plan, stored, saveContext),
      });
    }

    // #3483: duplicate-subSlotId invariant at the write boundary. The pre-fix add
    // path could push an id already referenced by a workout (root of the duplicate
    // $custom$108). Like the orphan canary above, this fires only when a write path
    // hands us a plan with duplicate ids — loadPlanAsync's dedup heal cleans latent
    // data before it reaches here, so this catches *new* write-path regressions with
    // their own breadcrumbs. Heal in place too, so we never persist a broken plan.
    const dupWorkouts: string[] = [];
    // for each workout with duplicate ids, record its name and heal in place
    for (const workout of (plan.workouts || [])) {
      if (hasDuplicateIds(workout.subSlotIds || [])) {
        dupWorkouts.push(workout.name);
        workout.subSlotIds = uniq(workout.subSlotIds);
      }
    }
    if (dupWorkouts.length > 0) {
      this.logging.trackSilentProblem('plan.duplicate-subslotid-at-save', {
        transaction,
        dupWorkouts,
        totalWorkouts: plan.workouts.length,
      });
    }
    return this.database.saveSingletonAsync(transaction, currentPlanId);
  }

  getEmptySchedule(): Schedule {
    return emptySchedule();
  }

  async loadScheduleAsync(): Promise<Schedule> {
    return this.database.withDocWatcher(watcher =>
      this.database.getSingleton(scheduleId, watcher));
  }

  async loadCustomMeasuresAsync() {
    return this.database.withDocWatcher(watcher =>
      this.database.getSingleton(customMeasuresId, watcher));
  }

  async saveScheduleAsync(transaction: TransactionId) {
    await this.database.saveSingletonAsync(transaction, scheduleId);
    return this.clearBacklogCacheAsync();
  }

  async getOneLogEntryAsync<T extends Log_Entry>(eid: string, watcher: DocWatcher): Promise<T> {
    const entry = await this.backlogCache.getEntryByIdAsync(eid);
    if (entry?._id) this.database.registerDocWithWatcher(entry._id, watcher);
    return entry as T;
  }

  // get the earliest log entry date (for "All" date range in progress charts)
  async getEarliestLogDateAsync(): Promise<DateTime | undefined> {
    const firstLogs: any[] = await this.database.withDocWatcherAsync(watcher =>
      this.database.getByIdHeadAsync('log/', watcher, { limit: 1 }));
    if (firstLogs.length === 0) {
      return undefined;
    }
    const firstLog = firstLogs[0] as Log_Entry;
    return DateTime.fromISO(firstLog.time_utc).toLocal();
  }

  /**
   * Whether any workout document was saved with a UTC time before the start of the given UTC
   * day (`YYYY-MM-DD`). Reads ids only, so it costs a key scan and no document bodies.
   *
   * It answers only for what this device can read. Before the background sync has fetched the
   * older history, the local copy may hold an old day the user opened while the days around
   * it are still missing, so "none found" would prove nothing. Check
   * `database.isBackgroundSyncComplete()` before trusting a `false` (df-afa259234d1a).
   * THROWS `DatabaseUnavailableError` when the read could not run.
   */
  async hasSavedWorkoutBeforeUtcDayAsync(utcDay: string): Promise<boolean> {
    // log ids are `log/<time_utc>-<type>`, so they sort by time and an id before
    // `log/<utcDay>` was logged before that day began
    const ids = await this.database.getIdsByIdRangeAsync('log/', `log/${utcDay}`);
    return logIdsIncludeWorkout(ids);
  }

  assureIdForEntry(le: Log_Entry) {
    if (le._id == null) {
      if (
        !DateTime.fromISO(le.time_utc).isValid ||
        ![LogRecordType.DayStart, LogRecordType.DayEnd, LogRecordType.Meal, LogRecordType.WeighIn, LogRecordType.Workout, LogRecordType.WeeklyCheckup].some(
          e => e === le.type,
        )
      ) {
        throw new Error(`Log entry invalid for saving: ${JSON.stringify(le, null, 2)}`);
      }
      le._id = 'log/' + le.time_utc + '-' + le.type;
    }
  }

  async saveEntryAsync(transaction: TransactionId, le: Log_Entry) {
    this.assureIdForEntry(le);
    await this.database.saveRecordAsync(le, transaction);

    // Update cache for this entry
    await this.dayTaskStatus.updateTaskStatusCacheForEntriesAsync([le]);

    // notify subscribers
    this.pubSub.publish(logEntrySavedEvent, le);
  }

  async saveEntriesAsync(transaction: TransactionId, entires: Log_Entry[]) {
    const saveResult = await this.database.saveBulkAsync(transaction, entires);
    // failed rows are handled INSIDE saveBulkAsync since #3987 A1: conflicted
    // rows get a merge-retry (a recovered row comes back rewritten as ok:true
    // with its new rev), and every genuinely lost row was already reported
    // there via trackSilentProblem('saveBulkAsync: write dropped') — so this
    // caller must not re-report (it used to, which double-counted drops).
    saveResult.forEach((r, i) => {
      if (r.ok && r.rev) {
        entires[i]._rev = r.rev;
      }
    });

    // Update cache for affected dates instead of invalidating
    await this.dayTaskStatus.updateTaskStatusCacheForEntriesAsync(entires);

    return saveResult;
  }

  // store one telemetry event or user data update into local db
  // (we do it this way so that we can send them in batches and make sure it works even when offline)
  // answers with what became of the write (df-068808a2796e). The ordinary queue save can
  // ignore it, but the server-side delegation copy must not: nothing saves that copy again,
  // so a write the database repair swallowed means the copy does not exist (df-7f5d5c076760)
  async saveTelemetryEventAsync(record: TelemetryData): Promise<WriteOutcome> {
    // if offline retry re-creates the event object, ensure we use the canonical reference from the identity map
    record = this.database.ensureCachedReference(record);
    return this.database.saveRecordAsync(record, DatabaseWrapper.getTransactionId());
  }

  async getTelemetryDataAsync(): Promise<TelemetryData[]> {
    return this.database.withDocWatcherAsync(watcher =>
      this.database.getByIdHeadAsync('mix/', watcher));
  }

  /** Deletes the given stored telemetry events, and answers with the ones that were NOT deleted. */
  async deleteTelemetryAsync(telemetryDataList: TelemetryData[]): Promise<TelemetryData[]> {
    return this.database.deleteRecordsAsync(telemetryDataList, DatabaseWrapper.getTransactionId());
  }

  async getTelemetryDataServerSideCountAsync() {
    const serversideData = await this.database.getAllOfType('mix-server');
    return serversideData.length;
  }

  async createCurrentPlanBackupLog(transaction: TransactionId): Promise<FitnessPlan> {
    // the deepClone stays inside the body so the watcher is released at the same point
    // it always was — after the clone, not between the read and the clone.
    const planBackup: FitnessPlan = await this.database.withDocWatcherAsync(
      async watcher => deepClone(await this.loadPlanAsync(watcher)));
    // the snapshot keeps its own workouts and subSlots, but drops the generated-program
    // backup: nothing ever reads that field out of a journal entry, and carrying it made
    // every snapshot store the program a second time (df-cfa9c57d3c50). Safe here only
    // because planBackup is already a clone — see the function's own warning.
    stripGeneratedPlanBackupForJournal(planBackup);
    // Atomically create unique journal entry - sets planBackup._id
    await this.database.createUniqueJournalEntryAsync(planBackup, 'FitnessPlan', transaction, true);
    return planBackup;
  }

  async createIntakeFormStateLogAsync(intakeFormState: IntakeFormState, transaction: TransactionId) {
    const logEntry = deepClone(intakeFormState);
    // same reason as in createCurrentPlanBackupLog: the plans embedded in this snapshot
    // keep everything except their generated-program backup, which no reader takes from a
    // journal entry (df-cfa9c57d3c50). Safe here only because logEntry is already a clone.
    stripGeneratedPlanBackupsForJournal(logEntry);
    try {
      // Atomically create unique journal entry - sets logEntry._id
      await this.database.createUniqueJournalEntryAsync(logEntry, 'IntakeForm', transaction);
    } catch (err) {
      this.logging.trackAbnormalException(err);
    }
  }

  async saveIntakeFormStateLogAsync(intakeFormState: IntakeFormState, transaction: TransactionId): Promise<void> {
    // awaited rather than returned: saveRecordAsync now answers with what became of the
    // write (df-068808a2796e), and that answer is for the delete paths, not for here.
    await this.database.saveRecordAsync(intakeFormState, transaction);
  }

  async getIntakeFormLogEntryAsync(logId: string): Promise<IntakeFormState> {
    return this.database.withDocWatcherAsync(watcher =>
      this.database.getRecordAsync(logId, watcher));
  }

  async getFitnessPlanBackupEntryAsync(logId: string): Promise<FitnessPlan> {
    return this.database.withDocWatcherAsync(watcher =>
      this.database.getRecordAsync(logId, watcher));
  }

  createGenericEntry(date: DateTime, type: LogRecordType, name?: string): Log_Entry {
    return createGenericEntry(date, type, name);
  }

  createWorkoutEntry(date: DateTime): Log_Workout {
    return createWorkoutEntry(date);
  }

  createMealEntry(date: DateTime, name: string): Log_Meal {
    return createMealEntry(date, name);
  }

  createWeeklyCheckupEntry(date: DateTime): Log_WeeklyCheckup {
    return createWeeklyCheckupEntry(date);
  }

  /**
   * Bring one stored meal up to the shape today's code expects, as it enters typed
   * code. Every meal the app reads off disk passes through here, so this is where an
   * old document shape is repaired once instead of at each reader.
   */
  backCompatMealEntry(meal: Log_Meal) {
    if (meal['displayTargets']) {
      meal.planningTargets = meal['displayTargets'];
      delete meal['displayTargets'];
    }
    if (meal['mealFoods']) {
      meal.ingredients = meal['mealFoods'];
      delete meal['mealFoods'];
    }
    // a meal saved before the rename carried neither field, so it arrives with no
    // ingredient list at all. Give it one here. `Log_Meal.ingredients` is declared
    // non-optional, so the compiler tells every reader downstream that the field is
    // always there — a reader that believes it throws on a spread or a dereference,
    // usually on a path that discards its own promise, and the user sees an action
    // simply do nothing.
    // info rather than a problem, and why cs-day-items.ts reports the same condition at a
    // different severity: see the header of util/meal-ingredients.ts.
    const { shouldReport } = repairMealIngredientsFromStorage(meal, this.reportedMealsWithNoIngredientList);
    if (shouldReport) {
      this.logging.trackSilentInfo('meal-read-missing-ingredient-list', {
        mealId: meal._id,
        mealName: meal.name,
        state: meal.state,
      });
    }
  }

  backCompatWorkoutEntry(workout: Log_Workout) {
    // fixup eventual old slots
    if (workout.exercises) {
      for (const e of workout.exercises) {
        e.subSlotId = fixupSlotId(e.subSlotId);
      };
    }
  }

  backCompatLogEntry(entry: Log_Entry) {
    if (entry.type === LogRecordType.Meal) {
      this.backCompatMealEntry(entry as Log_Meal);
    } else if (entry.type === LogRecordType.Workout) {
      this.backCompatWorkoutEntry(entry as Log_Workout);
    }
  }
  backCompatLogEntries(entries: Log_Entry[]) {
    entries.forEach(e => this.backCompatLogEntry(e));
  }

  createDayStartEntry(date: DateTime): Log_DayStart {
    return createDayStartEntry(date);
  }

  async deleteLogEntryAsync(e: Log_Entry, transaction: TransactionId) {
    await this.database.deleteRecordAsync(e, transaction);

    // Update cache for the deleted entry
    await this.dayTaskStatus.updateTaskStatusCacheForEntriesAsync([e]);
  }

  markForDeleteLogEntiresAsync(entries: Log_Entry[]) {
    this.database.markForDeleteRecords(entries);
  }

  async deleteFoodAsync(e: Food, transaction: TransactionId) {
    await this.database.deleteRecordAsync(e, transaction);
  }

  /** Note that this can only find foods that this user has previously used!
   * To get any food, call FoodDb.getFoodByIdAsync().
   */
  async getUsedFoodByIdAsync(foodId: string): Promise<Food> {
    console.assert(typeof foodId === 'string' && foodId.startsWith('food/'));
    return this.database.withDocWatcherAsync(async watcher => {
      try {
        const food = await this.database.getRecordAsync(foodId, watcher);
        if (food) {
          normalizeFoodFormat(food);
        }
        return food;
      } catch (err) {
        if (!isPouchNotFound(err)) {
          // "not_found" is normal couch error if the food is not cached yet
          // an unavailable database is normal during logout/shutdown — background food
          // caching may still be in-flight. Match the TYPE rather than the message text,
          // whose wording lives in `DatabaseUnavailableReason` and could be reworded.
          if (isDatabaseUnavailable(err)) {
            console.log("Can't get food in userDb (database shutting down).");
          } else {
            // anything else (a corrupt document, an adapter failure) is unexpected, and the
            // caller will read the null as an ordinary cache miss — so report it, or nobody
            // ever learns the food went missing
            this.logging.trackSilentProblem('food.cached-read-failed', { foodId, ...errorReportFields(err) });
          }
        }
        return null;
      }
    });
  }

  // Get multiple foods from user cache by their ids. Uncached foods will be returned as null.
  async getCachedFoodsMapByIdsAsync(foodIds: string[]): Promise<{ [key: string]: Food | null }> {
    const foodsArray: Food[] = await this.database.withDocWatcherAsync(watcher =>
      this.database.getByIdListAsync<Food>(foodIds, watcher));

    let foodsMap: { [key: string]: Food | null } = {};
    for (const food of foodsArray) {
      const id = food._id;
      normalizeFoodFormat(food);
      foodsMap[id] = food;
    }
    return foodsMap;
  }

  async deleteSavedRecipeAsync(e: Recipe, transaction: TransactionId) {
    await this.database.deleteRecordAsync(e, transaction);
  }

  async getAllSavedRecipesAsync(): Promise<Recipe[]> {
    return this.database.withDocWatcherAsync(async watcher => {
      const values = await Promise.all([
        this.database.getByIdHeadAsync('meal/', watcher),
        this.database.getByIdHeadAsync('food/+UDM', watcher),
        this.database.getByIdHeadAsync('food/+UDR', watcher),
      ]);
      return values[0].concat(values[1]).concat(values[2]);
    });
  }

  async cacheFoodAsync(food: Food): Promise<Food> {
    const foodId = food._id;

    // if there's already a pending cache operation for this food, wait for it
    const pending = this.pendingCaches.get(foodId);
    if (pending) {
      return pending;
    }

    // create a new cache operation
    const cacheOperation = this.doCacheFoodAsync(food);
    this.pendingCaches.set(foodId, cacheOperation);

    try {
      return await cacheOperation;
    } finally {
      this.pendingCaches.delete(foodId);
    }
  }

  private async doCacheFoodAsync(food: Food): Promise<Food> {
    if (!this.database) return food;

    const foodId = food._id;

    // snapshot food properties before getRecordAsync — the async read can trigger
    // the identity map to overwrite food with stale PouchDB data if food is the
    // canonical reference for this document id
    const snapshot: Record<string, any> = {};
    for (const key of Object.keys(food)) {
      if (key !== '_id' && key !== '_rev') {
        snapshot[key] = (food as any)[key];
      }
    }

    // if already in user DB, update existing to preserve identity map reference
    const existing: Food | null = await this.database.withDocWatcherAsync(async watcher => {
      try {
        return await this.database.getRecordAsync(foodId, watcher);
      } catch (err) {
        // not_found is expected for new foods
        if (!isPouchNotFound(err)) {
          throw err;
        }
        return null;
      }
    });

    // if database was shut down during the async gap above
    if (!this.database) return food;

    if (existing) {
      // apply snapshot (not food, which may have been overwritten by identity map)
      for (const key of Object.keys(snapshot)) {
        (existing as any)[key] = snapshot[key];
      }
      existing.local = existing.local || {};
      await this.database.saveRecordAsync(existing, DatabaseWrapper.getTransactionId(), true);
      return existing;
    }

    // new food - clone and save
    const newFood = deepClone(food);
    newFood._rev = undefined;
    newFood.local = newFood.local || {};
    // re-canonicalize: if a stale canonical exists (e.g., from a previously deleted food),
    // merge data into it to prevent identity map bypass warnings
    const canonical = this.database.ensureCachedReference(newFood);
    delete (canonical as any)._deleted;
    await this.database.saveRecordAsync(canonical, DatabaseWrapper.getTransactionId(), true);
    return canonical;
  }


  async uncacheFoodAsync(food: Food) {
    await this.database.deleteRecordAsync(food, DatabaseWrapper.getTransactionId());
    return food;
  }

  async getAllFoodsAsync(): Promise<Food[]> {
    return this.database.withDocWatcherAsync(async watcher => {
      const values = await Promise.all([
        this.database.getByIdHeadAsync('meal/', watcher),
        this.database.getByIdHeadAsync('food/', watcher),
      ]);
      return values[0].concat(values[1]);
    });
  }

  async getUserFoodHeadersByEANsAsync(eans: string[]): Promise<FoodHeader[]> {
    const results = await this.database.runQueryAsync('userFoodsByEAN', eans, { include_docs: true });

    return results.map(r => FoodToHeader(r.doc));
  }

  backCompatCustomExercise(exercise: CustomExercise) {
    // remap all slots that might be obsolete
    if (exercise && exercise.usedInSlots) {
      exercise.usedInSlots = exercise.usedInSlots.map(slotId => fixupSlotId(slotId));
    }
  }

  // answers the wrapper's WriteOutcome; most callers ignore it (see pass-through-db.md, WriteOutcome)
  async saveCustomExerciseAsync(exercise: CustomExercise, transaction: TransactionId): Promise<WriteOutcome> {
    return this.database.saveRecordAsync(exercise, transaction);
  }

  async deleteCustomExerciseAsync(exercise: CustomExercise, transaction: TransactionId) {
    await this.database.deleteRecordAsync(exercise, transaction);
  }

  async getAllCustomExercisesAsync(): Promise<CustomExercise[]> {
    const allExercises: any[] = await this.database.withDocWatcherAsync(watcher =>
      this.database.getByIdHeadAsync('exercise/+UDE', watcher));
    allExercises.forEach(e => this.backCompatCustomExercise(e));
    return allExercises;
  }

  async getCustomExerciseByIdAsync(exerciseId: string): Promise<CustomExercise> {
    const customExercise: CustomExercise = await this.database.withDocWatcherAsync(watcher =>
      this.database.getTypedRecordAsync<CustomExercise>(exerciseId, watcher));
    this.backCompatCustomExercise(customExercise);
    return customExercise;
  }

  // #3808 — one-time, idempotent heal for spurious self-forks. Before the ownership fix,
  // editing the user's OWN legacy exercise (a `+UDE-` doc with no `hashSource`) was wrongly
  // treated as editing a foreign exercise and forked a duplicate that then showed the false
  // "original shared exercise is no longer available" banner. This collapses each such pair:
  // de-fork the fork IN PLACE (keeping its id, plan references, and progression) and
  // soft-delete the now-orphan base when nothing references it. Runs at most once per session
  // and writes only when it finds something to heal, so a clean account pays only a cheap
  // local read. Never throws — a failed cleanup must not block plan load. Skipped during coach
  // impersonation / managing-a-client so it only ever touches the real signed-in user's data
  // (and the flag is left unset so the real session still heals afterwards). `uid` comes from
  // the caller (app.component) because UserDb does not inject AuthService.
  async healSpuriousSelfForksAsync(uid: string | undefined): Promise<void> {
    if (this.spuriousSelfForkHealDone) return;
    if (this.database?.impersonationDb || this.getCoachManagingClient()) return;
    this.spuriousSelfForkHealDone = true;
    try {
      const { plan, customs } = await this.database.withDocWatcherAsync(async watcher => ({
        plan: (await this.database.getSingleton(currentPlanId, watcher)) as FitnessPlan,
        customs: await this.getAllCustomExercisesAsync(),
      }));
      // a base counts as "used by the plan" when any subSlot's exercise_id points at it
      const planExerciseIds = new Set<string>();
      for (const subSlot of Object.values(plan.subSlots || {})) {
        if (subSlot?.exercise_id) planExerciseIds.add(subSlot.exercise_id);
      }
      const heal = planSpuriousSelfForkHeal(customs, uid, id => planExerciseIds.has(id));
      if (heal.forkIdsToDefork.length === 0 && heal.baseIdsToSoftDelete.length === 0) return;
      const byId = new Map(customs.map(e => [e._id, e]));
      const transaction = DatabaseWrapper.getTransactionId();
      // de-fork each spurious fork in place
      for (const forkId of heal.forkIdsToDefork) {
        const fork = byId.get(forkId);
        if (!fork) continue;
        deforkSelfFork(fork);
        await this.saveCustomExerciseAsync(fork, transaction);
      }
      // soft-delete each orphan base
      for (const baseId of heal.baseIdsToSoftDelete) {
        const base = byId.get(baseId);
        if (!base) continue;
        base.virtuallyDeleted = true;
        await this.saveCustomExerciseAsync(base, transaction);
      }
      this.logging.trackSilentInfo('exercise.spurious-self-fork-healed', {
        deforkedCount: heal.forkIdsToDefork.length,
        softDeletedBaseCount: heal.baseIdsToSoftDelete.length,
        deforkedIds: heal.forkIdsToDefork,
        softDeletedBaseIds: heal.baseIdsToSoftDelete,
      });
    } catch (err: any) {
      // if the network dropped or a database recovery cut a read short, nothing is wrong: clear
      // the flag so the next call (warm resume or next launch) runs the heal again. It is
      // idempotent, so a run cut short part-way through writes is safe to repeat. Info tier,
      // not problem (df-622fe503ca9b).
      const deferReason = selfForkHealDeferReason(err);
      if (deferReason) {
        this.spuriousSelfForkHealDone = false;
        this.logging.trackSilentInfo('exercise.spurious-self-fork-heal-deferred', {
          reason: deferReason, err: String(err?.message ?? err),
        });
        return;
      }
      // non-fatal cleanup — surface so a systemic failure isn't invisible, but never block boot
      console.error('healSpuriousSelfForks failed', err);
      this.logging.trackSilentProblem('exercise.spurious-self-fork-heal-failed', {
        err: String(err?.message ?? err), stack: err?.stack,
      });
    }
  }

  /** Re-ID a custom exercise from the old name-based ID to a new SHA-based ID.
   * Updates all references across user's data (exerciseStats, fitnessPlan, workout logs, etc.). */
  async reIdCustomExerciseAsync(oldExercise: CustomExercise, newExercise: CustomExercise): Promise<void> {
    const oldId = oldExercise._id;
    const newId = newExercise._id;
    const transaction = DatabaseWrapper.getTransactionId();

    // every step below can throw — the reads reach DatabaseUnavailableError and
    // the saves can fail — which is why the whole method runs inside the helper:
    // it releases the watcher however this ends.
    await this.database.withDocWatcherAsync(async watcher => {
      // load fitness state and plan
      const fitnessState = await this.loadFitnessStateAsync(watcher);
      const fitnessPlan = await this.loadPlanAsync(watcher);

      // 1. rename key in exerciseStats
      if (fitnessState.exerciseStats[oldId]) {
        fitnessState.exerciseStats[newId] = fitnessState.exerciseStats[oldId];
        delete fitnessState.exerciseStats[oldId];
      }

      // 2. rename key in tempSwapHistory
      if (fitnessState.tempSwapHistory && fitnessState.tempSwapHistory[oldId]) {
        fitnessState.tempSwapHistory[newId] = fitnessState.tempSwapHistory[oldId];
        delete fitnessState.tempSwapHistory[oldId];
      }

      // 3. update exercise_id in oldSubslots
      if (fitnessState.oldSubslots) {
        for (const subslot of fitnessState.oldSubslots) {
          if (subslot.exercise_id === oldId) {
            // eslint-disable-next-line no-restricted-syntax -- custom-exercise rename: oldSubslots id-string change, exercise identity preserved
            subslot.exercise_id = newId;
          }
        }
      }

      // 4. update idExercise in declaredState slots
      const declaredState = await this.loadDeclaredStateAsync(watcher);
      if (declaredState.declaredStrength?.slots) {
        for (const slot of declaredState.declaredStrength.slots) {
          if (slot.idExercise === oldId) {
            slot.idExercise = newId;
          }
        }
      }

      // 5. update exercise_id in fitnessPlan subSlots
      if (fitnessPlan.subSlots) {
        for (const subSlotId of Object.keys(fitnessPlan.subSlots)) {
          const subSlot = fitnessPlan.subSlots[subSlotId];
          if (subSlot.exercise_id === oldId) {
            // eslint-disable-next-line no-restricted-syntax -- custom-exercise rename: subSlots id-string change, exercise identity preserved
            subSlot.exercise_id = newId;
          }
        }
      }

      // 6. update all Log_Workout documents
      const workouts = await this.getWorkoutsForExerciseAsync(oldId);
      for (const wo of workouts) {
        let modified = false;
        for (const ex of wo.exercises) {
          if (ex.exerciseId === oldId) {
            ex.exerciseId = newId;
            modified = true;
          }
          if (ex.tempSwapOriginalExerciseId === oldId) {
            ex.tempSwapOriginalExerciseId = newId;
            modified = true;
          }
        }
        if (modified) {
          await this.database.saveRecordAsync(wo, transaction);
        }
      }

      // 7. update similarExerciseId on other custom exercises referencing the old ID
      const allCustomExercises = await this.getAllCustomExercisesAsync();
      for (const ex of allCustomExercises) {
        if (ex.similarExerciseId === oldId) {
          ex.similarExerciseId = newId;
          await this.saveCustomExerciseAsync(ex, transaction);
        }
      }

      // 8. delete old exercise doc
      await this.deleteCustomExerciseAsync(oldExercise, transaction);

      // 9. save new exercise doc (with new _id, no _rev)
      await this.saveCustomExerciseAsync(newExercise, transaction);

      // 10. save updated fitness state, plan, and declared state
      await this.saveFitnessStateAsync(transaction);
      await this.savePlanAsync(transaction);
      await this.saveDeclaredStateAsync(transaction);
    });
  }

  /** Get all log entries in the range of given times. */
  async getLogEntriesInRangeAsync(start: DateTime, end: DateTime, watcher: DocWatcher, options?: { inLogEntriesTimeZone?: boolean }): Promise<Log_Entry[]> {
    // if navigating outside the priority sync window, trigger on-demand sync
    await this.ensureDateRangeSyncedAsync(start);

    const entries = await this.backlogCache.getEntriesInRangeAsync(start, end, options);
    for (const entry of entries) {
      if (entry._id) this.database.registerDocWithWatcher(entry._id, watcher);
    }
    return entries;
  }

  // trigger on-demand sync for a date range outside the priority sync window
  // if background sync hasn't completed yet
  private async ensureDateRangeSyncedAsync(date: DateTime): Promise<void> {
    if (!this.database || this.database.isBackgroundSyncComplete()) return;

    // compare in UTC to match how log IDs and the priority sync selector work
    const dateUtc = date.toUTC();
    const cutoffDate = DateTime.utc().minus({ months: DatabaseWrapper.PRIORITY_SYNC_MONTHS_BACK }).startOf('day');
    if (dateUtc >= cutoffDate) return; // within priority window

    // per-session tracking to avoid redundant pulls (scoped by database to handle account switches)
    // mark the entire +/- 7 day radius as synced so adjacent date navigation doesn't re-fetch
    const dbId = this.database.internalDatabaseIdentifier;
    const dateKey = dateUtc.toISODate();
    const cacheKey = `onDemandSync_${dbId}_${dateKey}`;
    if (sessionStorage[cacheKey]) return;

    const succeeded = await this.database.syncLogDateRangeAsync(dateUtc);
    if (succeeded) {
      // mark all dates in the synced radius to avoid redundant pulls for adjacent days
      for (let d = dateUtc.minus({ days: 7 }); d <= dateUtc.plus({ days: 7 }); d = d.plus({ days: 1 })) {
        sessionStorage[`onDemandSync_${dbId}_${d.toISODate()}`] = '1';
      }
      // clear cache for synced range so it reloads from disk (UTC dates to match clearDays)
      const days = new Set<string>();
      for (let d = dateUtc.minus({ days: 7 }); d <= dateUtc.plus({ days: 7 }); d = d.plus({ days: 1 })) {
        days.add(d.toISODate());
      }
      await this.backlogCache.clearDays(days);
    }
  }

  public async clearBacklogCacheAsync() {
    await this.backlogCache.clear();
  }

  /**
   * Rebuild the past days whose new entries were kept in memory before this session's sync
   * succeeded (#6280). Called once the sync has succeeded, so this rebuild reads the schedule and
   * entries the sync delivered, and saves those days.
   *
   * The cache is cleared first because the held days are already in it, built from the schedule
   * as it was before the sync, and a cached day is not rebuilt.
   */
  private async rebuildHeldPastDaysAsync(database: DatabaseWrapper, first: DateTime, last: DateTime): Promise<void> {
    // if another database was loaded while we waited, these days are not its days
    if (this.database !== database) return;
    await this.clearBacklogCacheAsync();
    await database.withDocWatcherAsync(watcher =>
      this.getLogEntriesInRangeAsync(first.startOf('day'), last.endOf('day'), watcher, { inLogEntriesTimeZone: true }));
  }

  /**
   * Re-save the days the home page opened before this session's sync had succeeded (#6828).
   * Called once the sync has succeeded.
   *
   * The cache is cleared first because it still holds the copies built before the sync, none of
   * them saved. Rebuilt from disk, an entry the sync brought in from another device is now a
   * saved one, so the re-save leaves it alone and saves only what no device has saved yet.
   */
  private async resaveHeldDaysAfterSyncAsync(database: DatabaseWrapper, first: DateTime, last: DateTime): Promise<void> {
    // if another database was loaded while we waited, these days are not its days
    if (this.database !== database) return;
    await this.clearBacklogCacheAsync();
    for (let day = first.startOf('day'); day <= last; day = day.plus({ days: 1 })) {
      await this.resaveCachedEntriesForDayAsync(day);
    }
  }

  /**
   * Re-saves cached log entries to the database for a specific day.
   * This is useful when opening a day from the home page to ensure all
   * scheduled entries that were only in memory get persisted to the database.
   * Only processes entries for today or past days, and only once this session's sync has
   * succeeded: a past day is saved by the rebuild that follows the sync (#6280), today by
   * `resaveHeldDaysAfterSyncAsync` (#6828).
   */
  public async resaveCachedEntriesForDayAsync(dayDate: DateTime): Promise<void> {
    // only process today or past days, and only once the sync has succeeded (#6280, #6828)
    const today = this.debug.getNowLocal().startOf('day');
    const decision = chooseResaveForDay(dayDate, today, this.database?.initialSyncCompletedState === true);
    // if today was opened before the sync succeeded, re-save it once the sync has succeeded
    if (decision === ResaveDayDecision.Hold) {
      this.todayResaveHold?.hold([dayDate.toISODate()]);
      return;
    }
    if (decision !== ResaveDayDecision.Save) {
      return;
    }

    // get all entries for this day from the cache
    const dayStart = dayDate.startOf('day');
    const dayEnd = dayDate.endOf('day');
    const cachedEntries = await this.backlogCache.getEntriesInRangeAsync(dayStart, dayEnd, { inLogEntriesTimeZone: true });

    // filter entries that are not in the database (no _rev field)
    const entriesToSave = cachedEntries.filter(entry => !entry._rev);

    if (entriesToSave.length === 0) {
      return;
    }

    // save entries to database in a single transaction
    const transaction = DatabaseWrapper.getTransactionId();
    for (const entry of entriesToSave) {
      this.assureIdForEntry(entry);
    }

    // ensure canonical documentCache references before saving
    const canonicalEntries = entriesToSave.map(entry => this.database.ensureCachedReference(entry));

    try {
      await this.saveEntriesAsync(transaction, canonicalEntries);
      console.log(`Re-saved ${canonicalEntries.length} cached entries to database for ${dayDate.toISODate()}`);
    } catch (error) {
      this.logging.trackAbnormalError(`Failed to re-save cached entries to database: ${error}`);
    }
  }

  // the saved log entries in a range, read straight from the database, bypassing the backlog cache.
  // A caller gets only what was saved (never the planned entries the cache fills in), an empty
  // list before the database exists, and a DatabaseUnavailableError when the read fails. Used by
  // generateBacklogInMemoryAsync and getRecentStoredLogEntriesAsync.
  private async getLogEntriesInRangeFromDiskAsync(start_local: DateTime, end_local: DateTime): Promise<Log_Entry[]> {
    if (this.database == null) {
      return []; // this happens at initial user creation - db is not ready when we get to the home screen. but nothing is in it anyway
    }

    /* Explanation:
    - currentDay is 00:00 in local time
    - log entries are in UTC
    - we are looking for all potential log entries that are between -01:00 and +25:00 hours relative to currentDay (the extra one hour on each side is due to possible DST changes)
    - UTC time can be from =local_time to local_time-23:00
    - so we take the current day's local date (without time), and get all entries whose UTC date is +/-1 day
    - then we do fine-grain filtering
    */

    // round the range up and down by 1 day
    const startMargin_utc = start_local.minus({ days: 1 });
    const endMargin_utc = end_local.plus({ days: 1 });

    // Read the whole range in ONE query instead of one query per day. Log
    // entry ids look like `log/YYYY-MM-DDThh:mm:ss.sssZ-<Type>`, so the
    // inclusive key range from `log/<firstDay>` to `log/<lastDay>` plus the
    // high U+FFF0 marker spans every entry on every day in between. That
    // trailing U+FFF0 on the end key matches the old per-day prefix scan's
    // endkey, so the last day's entries are still included. allDocs returns
    // each doc once, so no de-duplication across days is needed any more.
    //
    // Why this matters: on native each query is a round-trip over the single
    // Capacitor↔SQLite bridge. The old per-day loop made ~one round-trip per
    // day (e.g. ~90 for a 60-day request), and during the cold-start sync
    // write-storm each of those reads was starved one wait at a time — which
    // is what made the first morning open of the weigh-in page take ~10s. A
    // single range read waits only once.
    // an unavailable read THROWS (DatabaseUnavailableError) and propagates out
    // of this method — it is deliberately NOT converted back to an empty list,
    // because "the database was not available" and "there are no entries in this
    // range" must not look the same to a caller. Callers that display this must
    // be ready for the throw; the helper releases the watcher either way.
    const rawEntries = (await this.database.withDocWatcherAsync(diskWatcher =>
      this.database.getByIdRangeAsync(
        'log/' + startMargin_utc.toISODate(),
        'log/' + endMargin_utc.toISODate() + '\ufff0',
        diskWatcher,
      ))) as Log_Entry[];

    const allEntries = rawEntries.filter(e => isDayLogEntry(e));

    const filteredEntries = allEntries.filter(e => {
      const entryTime_local = getEntryLocalTimeInDesiredTimeZone(e, start_local);
      return entryTime_local >= start_local && entryTime_local <= end_local;
    });

    this.backCompatLogEntries(filteredEntries);

    return filteredEntries;
  }

  // Get all log entries of given type in the last several days.
  async getRecentLogEntries<T extends Log_Entry>(type: LogRecordType, now_local: DateTime, days: number): Promise<T[]> {
    const start = now_local.startOf('day').minus({ days: days }).toUTC();
    const end = now_local.endOf('day').toUTC();
    const entries: Log_Entry[] = await this.database.withDocWatcherAsync(watcher =>
      this.getLogEntriesInRangeAsync(start, end, watcher));

    const filtered = entries.filter(e => e.type === type) as T[];
    return filtered;
  }

  /**
   * The saved log entries of one type in the last several days, read straight from the database
   * instead of through the backlog cache. Oldest first, like `getRecentLogEntries`.
   *
   * Use it when the caller only cares about what the user actually logged. It does not return
   * entries that live only in memory: the planned entries the cache fills in for days that were
   * never saved.
   *
   * Why it exists: a range the cache does not hold yet is rebuilt day by day, and every rebuild
   * runs the schedule solver. Right after the app starts, Home has cached only a few weeks, so the
   * weigh-in page's 60-day history read made the cache rebuild 75 days. That took about 0.8 s on
   * the main thread (Chrome, CPU slowed 4x) and froze the page's slide-in on its first open.
   */
  async getRecentStoredLogEntriesAsync<T extends Log_Entry>(type: LogRecordType, now_local: DateTime, days: number): Promise<T[]> {
    const start_local = now_local.startOf('day').minus({ days: days });
    const end_local = now_local.endOf('day');
    // same on-demand sync as the cached read, for a window older than the priority sync covers
    await this.ensureDateRangeSyncedAsync(start_local);

    const entries = await this.getLogEntriesInRangeFromDiskAsync(start_local, end_local);
    const filtered = entries.filter(e => e.type === type && !e._deleted) as T[];
    // the disk read is in id order; sort by entry time, as the cache does, so the newest entry is last
    // (a damaged entry with no time sorts first rather than throwing)
    filtered.sort((a, b) => (a.time_utc ?? '').localeCompare(b.time_utc ?? ''));
    return filtered;
  }

  /** How long the engagement read may take before we give up and do not prompt. */
  private static readonly REVIEW_PROMPT_HISTORY_TIMEOUT_MS = 5_000;

  /**
   * A ceiling on rows, far above any real training history — 8 weeks is 56 days,
   * so this is close to 9 workouts a day. Hitting it is reported rather than
   * silently accepted, because a truncated read drops the newest weeks (the scan
   * is in id order, which is time order) and would refuse somebody who trains
   * perfectly well.
   */
  private static readonly REVIEW_PROMPT_HISTORY_MAX_ROWS = 500;

  /**
   * #4625 — how consistently this person has trained recently, for the one gate
   * that decides whether to ask them to rate the app.
   *
   * **One read, returning counts rather than answers.** The gate asks two
   * questions of it today ("a workout in at least 5 of the last 8 weeks" and
   * "trained in the last 10 days") and phase 2 will ask a third ("3 of the last
   * 4 weeks"), all from these same numbers. Deciding anything here would mean
   * coming back to this read every time a threshold moves.
   *
   * **A failed read comes back as "cannot tell", never as zero workouts.** Those
   * are different facts and the difference decides whether anybody should look
   * into it: zero is a real answer about a real person, while cannot-tell means
   * this device could not read its own database. Treating the second as the
   * first would refuse somebody with `NotTrainingConsistently`, sending whoever
   * investigated to look at a training history that is perfectly fine. Every
   * path that could not produce real counts returns `{}`, which the gate reads
   * as `EngagementUnknown`.
   *
   * **Bounded, because this is asked at a moment somebody is waiting on the
   * app** — as a set is concluded, or as a weekly checkup finishes. The caller
   * runs it off that path, and the ceiling here is the second guarantee: a read
   * that has hung can never leave a prompt pending for ever. It is deliberately
   * generous rather than tight, because the cost of giving up too early is a
   * prompt that silently never fires, and a feature that is not firing looks
   * exactly like a feature with no eligible audience.
   *
   * The view is already pre-warmed at boot by `warmUpViewsAsync`, so on the
   * normal path this costs a range scan of ids and no document reads at all.
   */
  async getWorkoutHistoryForReviewPromptAsync(now: DateTime): Promise<WorkoutHistory> {
    // an unusable clock cannot produce a window to read, let alone buckets to
    // put anything in. The gate refuses on it too, with its own named reason.
    if (!now || !now.isValid) return {};

    let history: WorkoutHistory = {};
    const readAsync = async (): Promise<void> => {
      history = await this.readWorkoutHistoryForReviewPromptAsync(now);
    };

    const outcome = await awaitSignalOrTimeoutAsync(
      readAsync(), sleep, UserDb.REVIEW_PROMPT_HISTORY_TIMEOUT_MS);
    if (outcome === FirstProbeGateOutcome.Timeout) {
      // reported rather than swallowed: a range scan over a pre-warmed view
      // taking this long means something is wrong with this device's database,
      // and the only other trace of it would be a prompt that never appeared.
      this.logging.trackSilentProblem('ReviewPromptHistoryTimedOut', {
        timeoutMs: UserDb.REVIEW_PROMPT_HISTORY_TIMEOUT_MS,
      });
      return {};
    }
    return history;
  }

  /** The read half of `getWorkoutHistoryForReviewPromptAsync`, without the time bound. */
  private async readWorkoutHistoryForReviewPromptAsync(now: DateTime): Promise<WorkoutHistory> {
    // the view emits each completed workout's document id, and that id is built
    // from the workout's own `time_utc`, so a range over ids IS a range over
    // time. The two bounds are built in `shared/` beside the window they have to
    // agree with, because a drift between them fails silently — see
    // `reviewPromptHistoryKeyRange`, which a test holds to the real id shape.
    const { startkey, endkey } = reviewPromptHistoryKeyRange(now);

    let rows: { id: string }[];
    try {
      rows = await this.database.runQueryAsync('completedWorkouts', undefined, {
        startkey, endkey,
        include_docs: false,
        limit: UserDb.REVIEW_PROMPT_HISTORY_MAX_ROWS,
      });
    } catch (err) {
      if (isDatabaseTornDown(err)) {
        // our own sign-out wipe is not a fault, and `trackSilentProblem` writes
        // a console.error the E2E browser-error gate fails on
        console.log(`Review-prompt history read skipped — our own teardown (${err.reason})`);
        return {};
      }
      if (isDatabaseUnavailable(err)) {
        this.logging.trackSilentProblem('ReviewPromptHistoryUnavailable', { reason: err.reason });
        return {};
      }
      // anything else is genuinely unexpected here. It is reported rather than
      // rethrown because the caller is a fire-and-forget "should we ask this
      // person?" and must never be able to break concluding a set — but it is
      // reported, so it cannot vanish.
      this.logging.trackSilentProblem('ReviewPromptHistoryReadFailed', errorReportFields(err));
      return {};
    }

    if (rows.length >= UserDb.REVIEW_PROMPT_HISTORY_MAX_ROWS) {
      this.logging.trackSilentProblem('ReviewPromptHistoryTruncated', {
        limit: UserDb.REVIEW_PROMPT_HISTORY_MAX_ROWS,
      });
    }

    const workoutTimes: DateTime[] = [];
    let unreadableIds = 0;
    for (const row of rows) {
      // getEntryTimeFromID throws on an id that does not match the expected
      // shape, so it is guarded rather than trusted. An id we cannot read is
      // counted and reported: silently dropping them would undercount weeks and
      // refuse somebody for a reason that has nothing to do with their training.
      try {
        const time = getEntryTimeFromID(row.id);
        if (time && time.isValid) workoutTimes.push(time);
        else unreadableIds++;
      } catch {
        // counted here, reported once after the loop as ReviewPromptHistoryUnreadableIds
        unreadableIds++;
      }
    }
    if (unreadableIds > 0) {
      this.logging.trackSilentProblem('ReviewPromptHistoryUnreadableIds', {
        unreadableIds, rows: rows.length,
      });
    }

    return summarizeWorkoutHistory(workoutTimes, now);
  }

  // check if the user has completed at least n workouts
  async hasCompletedNWorkoutsAsync(n: number): Promise<boolean> {
    const results = await this.database.runQueryAsync('completedWorkouts', undefined, {
      startkey: '', endkey: '\ufff0', limit: n,
    });
    return results.length >= n;
  }

  // Count of completed (Done) workouts belonging to ONE split day, capped at 3, optionally
  // excluding one workout id. Used by the onboarding payoff message (#4061) to tell that
  // split day's 1st completed run from its 2nd (and "later" from both).
  //
  // Counting per split day rather than across the user's whole history is the point: a plan
  // is a split (A/B/C), and every split day starts with its own strength test the first time
  // it is trained. A global count made only day A look like a first workout, so day B's very
  // first run wrongly claimed the user's starting weights were already set.
  //
  // A split day is identified POSITIONALLY, by its index into plan.workouts -- there is no
  // stable id for a training day. So when the solver regenerates the plan, older workouts
  // keep the indices they were saved with, and a genuinely-first strength test on a
  // regenerated day can look like a repeat and fall back to the generic message. That is
  // accepted: it is no worse than the global count this replaced (which was generic there
  // too), and the whole consequence is one onboarding message, with no data effect.
  //
  // The read is bounded to 3 rows -- 2 to detect ">=2 prior" plus 1 for the possibly-present
  // excluded current id -- instead of materializing every key for a long-time user (mirrors
  // the sibling hasCompletedNWorkoutsAsync's use of `limit`). Any 3 rows still yield >=2
  // after the exclusion, which is all the caller needs to answer "3rd or later". Excluding
  // the current workout's id makes the count independent of whether that workout has been
  // persisted as Done yet (the completion branch runs before the caller saves it). This
  // view emits the split-day index as the row key, so we filter on `row.id`.
  async getCompletedWorkoutCountForPlanReferenceAsync(planReference: number, excludeId?: string): Promise<number> {
    const key = String(planReference);
    const results = await this.database.runQueryAsync('completedWorkoutsByPlanReference', undefined, {
      startkey: key, endkey: key, limit: 3,
    });
    if (!excludeId) return results.length;
    return results.filter(r => r.id !== excludeId).length;
  }

  /**
   * #4816 — the most recently finished workout, or `undefined` when there is none.
   * Used only by the review prompt's test-mode tool, to explain what the
   * personal-record moment would have decided for it.
   */
  async getLatestFinishedWorkoutAsync(): Promise<Log_Workout | undefined> {
    const rows: { id: string; doc: Log_Workout }[] = await this.database.runQueryAsync('completedWorkouts', undefined, {
      startkey: '\ufff0', endkey: '', descending: true, limit: 1, include_docs: true,
    });
    return rows[0]?.doc;
  }

  /**
   * The time of the oldest finished workout on this device, or undefined when there is none. The
   * Training Overview (#5456) asks it to know whether older sessions exist than the ones it loaded.
   * It sees only Done workouts that are already on the device: while older logs are still
   * downloading the answer can be too recent.
   */
  async getOldestCompletedWorkoutTimeAsync(): Promise<string | undefined> {
    const rows: { id: string; doc: Log_Workout }[] = await this.database.runQueryAsync('completedWorkouts', undefined, {
      startkey: '', endkey: '\ufff0', limit: 1, include_docs: true,
    });
    return rows[0]?.doc?.time_utc;
  }

  /**
   * #4816 — each exercise's best estimated one-rep max over every OTHER workout,
   * from the `bestStrengthByExercise` view. An exercise with no earlier finished
   * set is absent from the answer, which the caller reads as "no best to beat".
   *
   * Reads index rows only, never workout documents. The first call on a device
   * builds the view, which reads every document once; the review prompt calls this
   * only for somebody its gate already allows, so that cost is rare.
   *
   * Throws `DatabaseUnavailableError` when the view cannot be read, so "could not
   * look" is never mistaken for "no earlier best".
   */
  async getBestStrengthElsewhereAsync(exerciseIds: string[], excludeWorkoutId: string): Promise<Map<string, number>> {
    const rows = await this.database.runQueryAsync('bestStrengthByExercise', exerciseIds);
    const bestByExercise = new Map<string, number>();
    for (const row of rows) {
      // if the row is this workout, or its value is damaged, it says nothing about an earlier best
      if (row.id === excludeWorkoutId || typeof row.value !== 'number' || !isFinite(row.value)) continue;
      if (row.value > (bestByExercise.get(row.key) ?? 0)) bestByExercise.set(row.key, row.value);
    }
    return bestByExercise;
  }

  // check if the user has completed at least n workouts containing bodyweight exercises
  async hasCompletedNBwWorkoutsAsync(n: number): Promise<boolean> {
    const results = await this.database.runQueryAsync('completedBwWorkouts', undefined, {
      startkey: '', endkey: '\ufff0', limit: n,
    });
    return results.length >= n;
  }

  /**
   * The newest `limit` logged workouts containing this exercise, NEWEST FIRST.
   *
   * `getWorkoutsForExerciseAsync` below returns the exercise's ENTIRE history and fetches a document
   * for every workout it ever appeared in, which for a long-standing user is hundreds of documents.
   * That is affordable off the critical path, which is where its one caller runs. The #5176
   * prescription cap needs its answer BEFORE the workout page draws, and only ever looks at a
   * handful of recent sessions, so it reads through the index instead: `descending` plus `limit`
   * pushes the bound down into the view scan and fetches only the documents it will actually read.
   *
   * The bound has to go through the startkey/endkey path, NOT `keys`: on PouchDB's multi-key path
   * `descending` is ignored and `limit` is applied only after every row for the key has already been
   * fetched with its document, so it would save nothing. Descending swaps the roles of the two keys;
   * they are the same value here, which is why both read the same.
   *
   * Rows for one key come back in document-id order, and a workout's id starts with its UTC
   * timestamp, so the index order IS time order — that is what makes "newest" meaningful here.
   */
  async getRecentWorkoutsForExerciseAsync(exerciseId: string, limit: number): Promise<Log_Workout[]> {
    const rows: { id: string; key: string; doc: Log_Workout }[] = await this.database.runQueryAsync(
      'workoutByExerciseId', undefined,
      { startkey: exerciseId, endkey: exerciseId, descending: true, limit, include_docs: true });

    // a null doc happens when a workout was deleted between the index build and this read
    return uniqueWorkoutDocs(rows);
  }

  /**
   * A coach's or the user's saved changes to one program entry (#6428), newest first. Throws
   * `DatabaseUnavailableError` when the read fails, like every read here.
   */
  async getExerciseChangesForSubSlotAsync(subSlotId: string, limit: number): Promise<ExerciseChangeDoc[]> {
    const rows: { id: string; doc: ExerciseChangeDoc }[] = await this.database.runQueryAsync(
      'exerciseChangeBySubSlot', undefined,
      { startkey: [subSlotId, '\uffff'], endkey: [subSlotId], descending: true, limit, include_docs: true });
    // a null doc happens when a document was deleted between the index build and this read
    return rows.map(row => row.doc).filter(doc => !!doc);
  }

  async getWorkoutsForExerciseAsync(exerciseId: string): Promise<Log_Workout[]> {
    const rows: { id: string; key: string; doc: Log_Workout }[] = await this.database.runQueryAsync('workoutByExerciseId', [exerciseId], { include_docs: true });

    // filter out rows with null doc (can happen if a workout was deleted between index build and query)
    return uniqueWorkoutDocs(rows);
  }

  // force PouchDB to build view indexes in background so they're ready
  // when the user opens a workout (avoids cold-start delay on first query)
  async warmUpViewsAsync(): Promise<void> {
    const views = ['workoutByExerciseId', 'completedWorkouts', 'completedBwWorkouts', 'completedWorkoutsByPlanReference'];
    await Promise.allSettled(views.map(view =>
      this.database.runQueryAsync(view, undefined, { startkey: '__warmup__', endkey: '__warmup__' })
        .catch(e => console.log(`View warm-up failed for ${view} (non-critical):`, e))
    ));
  }

  async loadOldPlansAsync(): Promise<FitnessPlanWithTimestamp[]> {
    let le: { id: string; key: string; doc: FitnessPlan }[] =
      await this.database.runQueryAsync('planBackupById', undefined, {
        startkey: 'log/',
        endkey: 'log/\uffff',
        include_docs: true,
      });
    // backcompat all plans
    for (const planHolder of le) {
      const plan = planHolder.doc;
      let needsSaving = await this.backCompatFitnessPlan(this.database, plan, this.fitnessState);
      if (needsSaving) {
        planHolder.doc = plan;
      }
    }

    // the ids are in form 'log/2024-05-03T20:09:15.360Z-FitnessPlan', extract only the timestamp
    const oldPlans: FitnessPlanWithTimestamp[] = le.map(le => {
      return { ...le.doc, timestamp_utc: le.id.match(/log\/(.+)-FitnessPlan/)[1] };
    });

    return oldPlans;
  }

  /**
   * What one meal should contain, given what the rest of its day has already used up (#5297).
   *
   * The reason this exists rather than callers reading `meal.planningTargets`: that stored field
   * is computed with the meal's OWN food counted into the day total, so it answers "what should
   * still go into this slot". Correct while the slot is empty, wrong once it is not. See
   * `mealTargetsAsIfEmpty` for the measured example.
   *
   * Reads the day from the database. A caller that already has the day's meals in memory should
   * call `mealTargetsAsIfEmpty` directly instead of paying for this read.
   */
  async getMealTargetsAsIfEmptyAsync(meal: Log_Meal): Promise<NutrientTargets> {
    const time = getEntryLocalTimeInDesiredTimeZone(meal, this.debug.getNowLocal());
    const { day, meals } = await this.getDayMealsAsync(time);
    // `day` is absent when the range held no DayStart entry, which is a real state rather than a
    // fault — every other caller of getDayMealsAsync guards it. `actualMealTargets` copes with
    // empty day targets by falling back to the meal's own, so an answer is still worth giving.
    return mealTargetsAsIfEmpty(meal, meals, day?.targets || {}, time);
  }

  /**
   * `getMealTargetsAsIfEmptyAsync` for a button or menu handler: a failed read is reported to the
   * user with `userMessageOnFailure` and to Sentry, and the answer is `undefined`.
   *
   * Nothing awaits those handlers, so a read failure left to escape reaches the user as a button
   * that silently does nothing — which is how Auto-Tune shipped once. The caller leaves when the
   * answer is `undefined` and still checks its own destroyed-view flag after the await.
   */
  async getMealTargetsAsIfEmptyOrReportAsync(
    meal: Log_Meal,
    userMessageOnFailure: TranslatedString,
  ): Promise<NutrientTargets | undefined> {
    try {
      return await this.getMealTargetsAsIfEmptyAsync(meal);
    } catch (err) {
      this.logging.trackAbnormalException(err, userMessageOnFailure);
      return undefined;
    }
  }

  /**
   * The ids of every meal FILED on a day, which is not the same question as which meals that day
   * SHOWS.
   *
   * A log entry's document id names the instant it was created at and never changes when the entry
   * is moved, while the day an entry is shown on comes from the wall-clock time on it. After a
   * time-zone change, or a meal moved across midnight, the two part company: a meal filed on this
   * day can be shown on the day before or after, and `getDayMealsAsync` — which selects by the time
   * shown — will not return it. Anything MINTING a new id for this day has to avoid those ids as
   * well, or it builds an id another meal already holds and is saved over it (#5466).
   *
   * So this asks by id alone: the same backlog-cache read as `getDayMealsAsync`, but narrowed to
   * this day and WITHOUT the wall-clock filter. The cache holds both stored meals and the ones the
   * schedule rebuild has so far only in memory.
   */
  async getMealIdsFiledOnDayAsync(timeLocal: DateTime): Promise<Set<string>> {
    const dayLocal = timeLocal.startOf('day');
    const entries: Log_Entry[] = await this.database.withDocWatcherAsync(watcher =>
      this.getLogEntriesInRangeAsync(dayLocal, dayLocal.endOf('day'), watcher));
    return new Set(entries.filter(e => e.type === LogRecordType.Meal).map(e => e._id));
  }

  /** For any time inside a day, load the holder with daily values for that day, and all meals for that day.  */
  async getDayMealsAsync(timeLocal: DateTime): Promise<{ day: Log_DayStart; meals: Log_Meal[] }> {
    // Which entries belong to a day is decided by the time the user SEES on them — their
    // wall-clock time — not by the instant buried in their document key. The two are the same only
    // while every entry was written on a device in this time zone. When they differ, reading by
    // document key silently picks up the tail of the previous day and drops the end of this one,
    // and it answers differently on a coach's device than on their client's (#4728).
    //
    // Getting that requires all three steps below, in this order. The widening is not optional:
    // the wall-clock filter can only remove entries from what the key range already selected, so
    // without it the entries this day needs are never fetched in the first place. It is cheap —
    // the cache fills +/-7 days anyway, and the day view already issues this same widened read.
    const dayLocal = timeLocal.startOf('day');
    const targetDate = dayLocal.toISODate();

    const entries: Log_Entry[] = await this.database.withDocWatcherAsync(watcher =>
      this.getLogEntriesInRangeAsync(
        dayLocal.minus({ days: 1 }),
        dayLocal.plus({ days: 1 }).endOf('day'),
        watcher,
        { inLogEntriesTimeZone: true },
      ));

    // narrow the widened read back to the single day the caller asked about
    const onTargetDay = entries.filter(
      e => getEntryLocalTimeInDesiredTimeZone(e, dayLocal).toISODate() === targetDate,
    );

    // pick the day's OWN DayStart — the read now spans three days, so taking the first one found
    // would hand back a neighbouring day's entry and write this day's totals onto it.
    // Which of the day's own day starts, when it holds two, is asked through the SHARED rule:
    // the day list on screen asks the same question, and answering it differently here showed
    // the user two different calorie targets for one day (#4511).
    const day = this.keepOneEntryPerSingletonSlot(onTargetDay, dayLocal)
      .find(e => e.type == LogRecordType.DayStart) as Log_DayStart;
    const meals = onTargetDay.filter(e => e.type == LogRecordType.Meal) as Log_Meal[];

    return { day, meals };
  }

  /** Update daily values from all entries in that day as needed. */
  async updateDayValuesOnDiskAsync(transaction: TransactionId, timeLocal: DateTime): Promise<Log_DayStart> {
    let { day, meals } = await this.getDayMealsAsync(timeLocal);

    if (!day || meals.length < 1) {
      console.error("Can't update day values, no data found.");
      return null;
    }

    let modifiedMeals: boolean[] = [];
    this.updateDayValuesInMemory(day, meals, modifiedMeals);

    let entriesToSave = [];
    let i = -1; // starting from -1 to match the loop in updateDayValuesInMemory()
    for (const m of meals) {
      i++;
      if (modifiedMeals[i]) {
        entriesToSave.push(m);
      }
    }
    entriesToSave.push(day);
    await this.saveEntriesAsync(transaction, entriesToSave);

    return day;
  }

  /**
   * Reduce a day's once-a-day events to at most one document each, and report the ones set aside.
   *
   * Every screen that shows or reads one of these events asks the SHARED rule which of two
   * documents to use, because answering it differently on two screens once showed one user two
   * different calorie targets for the same day (#4511). This wrapper is how a screen asks it: the
   * rule itself lives in `shared/`, which cannot report anything, so the report is added here and
   * the only shape a caller can reach is one that has already reported (df-97a26a40a3e0).
   *
   * Each set-aside document is reported at most once per session. A duplicate document is never
   * deleted, so without the dedup one stray document would report on every backlog regeneration,
   * for every day in the window, for as long as the account exists. Which tier each report goes
   * to, and why there is a key per document rather than one per set, are in
   * `describeDroppedSingletonEntries`.
   */
  keepOneEntryPerSingletonSlot(entries: Log_Entry[], dayReferenceTime: DateTime): Log_Entry[] {
    const { kept, dropped } = pickOneEntryPerSingletonSlot(entries, dayReferenceTime);

    const report = describeDroppedSingletonEntries(dayReferenceTime, dropped);
    // if any of these documents has not been reported yet, report the set and remember all of them
    if (report && report.reportKeys.some(key => !this.reportedDuplicateSingletonDays.has(key))) {
      for (const key of report.reportKeys) {
        this.reportedDuplicateSingletonDays.add(key);
      }
      if (report.isUnresolvedProblem) {
        this.logging.trackSilentProblem(report.name, report.context);
      } else {
        this.logging.trackSilentInfo(report.name, report.context);
      }
    }

    return kept;
  }

  // Report the meals the one-per-slot collapse left out of the day's total. Which name and tier
  // each drop gets is `describeDroppedDuplicateMeals`.
  //
  // Reported at most once per day per session, because a duplicate is never deleted: without the
  // dedup one stray document reports on every backlog regeneration, for every day in the window,
  // for as long as the account exists.
  private reportDroppedDuplicateMeals(dayReferenceTime: DateTime, kept: Log_Meal[], dropped: Log_Meal[]) {
    const report = describeDroppedDuplicateMeals(dayReferenceTime, kept, dropped);
    if (!report) return;
    if (this.reportedDuplicateMealDays.has(report.reportKey)) return;
    this.reportedDuplicateMealDays.add(report.reportKey);

    if (report.isUnresolvedProblem) {
      this.logging.trackSilentProblem(report.name, report.context);
    } else {
      this.logging.trackSilentInfo(report.name, report.context);
    }
  }

  // Rename and save the stored meals that a meal of the same name hides from their day (#6207).
  //
  // Runs on the STORED documents, before the rebuild changes anything in memory, so the save writes
  // the stored body with only the new name. Saving the rebuild's output instead would also write its
  // time and target changes on past days, which the backlog never saves on purpose. Which meals
  // qualify, and why schedule-built twins are left alone, is `renameHiddenDuplicateMeals`. Once
  // saved, the day holds no such pair, so a later session finds nothing to do. Each repair is
  // reported at the info tier, so a new source of duplicates (#6208) still shows up.
  private async repairHiddenDuplicateMealsAsync(storedEntries: Log_Entry[], dayReferenceTime: DateTime) {
    const mealsByDay = new Map<string, Log_Meal[]>();
    for (const entry of storedEntries) {
      if (entry.type !== LogRecordType.Meal) continue;
      const day = getEntryLocalTimeInDesiredTimeZone(entry, dayReferenceTime).toISODate();
      mealsByDay.set(day, [...(mealsByDay.get(day) ?? []), entry as Log_Meal]);
    }

    const repairs: { day: string; renamed: { meal: Log_Meal; oldName: string }[] }[] = [];
    for (const [day, meals] of mealsByDay) {
      const renamed = renameHiddenDuplicateMeals(meals, dayReferenceTime);
      if (renamed.length > 0) {
        repairs.push({ day, renamed });
      }
    }
    if (repairs.length === 0) return;

    const renamedMeals = repairs.flatMap(r => r.renamed.map(x => x.meal));
    const saveResult = await this.saveEntriesAsync(DatabaseWrapper.getTransactionId(), renamedMeals);
    // a row saveBulkAsync dropped is already reported there as a problem, and the next disk read
    // brings its old name back, so only a row that really saved counts as repaired
    const savedIds = new Set(renamedMeals.filter((_meal, i) => saveResult[i]?.ok).map(meal => meal._id));
    for (const { day, renamed } of repairs) {
      const saved = renamed.filter(r => savedIds.has(r.meal._id));
      if (saved.length === 0) continue;
      this.logging.trackSilentInfo('day-total-duplicate-meal-slot-repaired', {
        day,
        renamedCount: saved.length,
        renamed: saved.map(r => `${r.meal._id}:${r.oldName}->${r.meal.name}`).join(','),
      });
    }
  }

  /** Update daily values from all entries in that day as needed.
   * The modified array is set to the same size as meals and marks which meal's targets were modified.
   */
  updateDayValuesInMemory(day: Log_DayStart, meals: Log_Meal[], modifiedMeals: boolean[]) {
    // Sum ONE meal per slot, using the same rule the day's card list uses, so the total at the top
    // of a day always equals the meals shown underneath it. A day can hold two documents for the
    // same meal, and neither may be deleted (see pickOneMealPerSlot). Summing both is what made a
    // customer see a total no combination of his visible meals added up to (#4728).
    const dayReferenceTime = getEntryLocalTimeInDesiredTimeZone(day, this.debug.getNowLocal());
    const { kept: mealsToSum, dropped: duplicateMeals } = pickOneMealPerSlot(meals, dayReferenceTime);
    this.reportDroppedDuplicateMeals(dayReferenceTime, mealsToSum, duplicateMeals);

    day.nutrients = {};
    for (const m of mealsToSum) {
      if (m.state == LogRecordState.Skipped) {
        continue;
      }
      for (let tag in m.nutrients) {
        if (!day.nutrients[tag]) {
          day.nutrients[tag] = 0;
        }
        day.nutrients[tag] += m.nutrients[tag];
      }
    }

    // The planning-target pass below stays over the FULL meals array: both callers map these
    // indexes straight back onto the array they passed in to decide what to save, so collapsing
    // here would misalign them and corrupt the save set.
    modifiedMeals.splice(0, modifiedMeals.length);
    let i = -1;
    for (const m of meals) {
      i++;
      modifiedMeals[i] = false;
      const otherMealTargets = meals.filter(x => x.nutrients[INFOODS.Calories] == undefined && x._id !== m._id).map(m => m.targets);
      const newPlanningTargets = actualMealTargets(m.targets, day.nutrients, day.targets, otherMealTargets);
      if (!deepCompareEQ(m.planningTargets, newPlanningTargets)) {
        m.planningTargets = newPlanningTargets;
        modifiedMeals[i] = true;
      }
    }
  }

  /**
   * Adds every day in `scheduledLogEntries` up again from the meals actually stored for it, and
   * refreshes those meals' planning targets, in memory only.
   *
   * This is what lets a writer leave a day's own summary alone and still have the user see the
   * right total: the meal-plan page relies on it by name, because every reader of a day goes
   * through the backlog cache and the cache is refilled by `generateBacklogInMemoryAsync`
   * (mh-physique#5106, and the comment in `meal-plan.ts` that names this method).
   *
   * `combinedEntriesRange` supplies meals the caller is not otherwise carrying; a meal whose
   * targets changed and that came only from there is appended to `scheduledLogEntries`, so the
   * caller saves it. Pass the same array for both when there is nothing extra to pull in and
   * nothing is going to be saved — every meal is then already present and nothing is appended.
   */
  private recomputeDayTotalsInMemory(
    scheduledLogEntries: Log_Entry[],
    combinedEntriesRange: Log_Entry[],
    startDay_local: DateTime,
  ): void {
    const dayStartsForSaving = scheduledLogEntries.filter(e => e.type === LogRecordType.DayStart);
    for (const dayStartForSaving of dayStartsForSaving) {
      // Use getEntryLocalTimeInDesiredTimeZone to compare days correctly across timezone boundaries
      const dayStartLocalTime = getEntryLocalTimeInDesiredTimeZone(dayStartForSaving, startDay_local);
      const mealsForSaving = scheduledLogEntries.filter(
        e => e.type === LogRecordType.Meal && getEntryLocalTimeInDesiredTimeZone(e, startDay_local).hasSame(dayStartLocalTime, 'day'),
      );
      const mealsAll = combinedEntriesRange.filter(
        e => e.type === LogRecordType.Meal && getEntryLocalTimeInDesiredTimeZone(e, startDay_local).hasSame(dayStartLocalTime, 'day'),
      );
      const meals = unionBy(mealsForSaving, mealsAll, e => e._id).sort((a: Log_Entry, b: Log_Entry) => (a.time_utc <= b.time_utc ? -1 : 1)) as Log_Meal[];
      let modifiedMeals: boolean[] = [];
      this.updateDayValuesInMemory(dayStartForSaving as Log_DayStart, meals, modifiedMeals);
      let i = -1; // starting from -1 to match the loop in updateDayValuesInMemory()
      for (const m of meals) {
        i++;
        if (modifiedMeals[i] && !mealsForSaving.find(e => e._id === m._id)) {
          scheduledLogEntries.push(m);
        }
      }
    }
  }

  async generateBacklogInMemoryAsync(
    firstDay_local: DateTime,
    lastDay_local: DateTime,
    cachedEntriesSnapshot: Log_Entry[],
  ): Promise<Log_Entry[]> {

    //console.log(`Generating backlog from ${firstDay.toISO()} to ${lastDay.toISO()}`);

    const startDay_local = firstDay_local.startOf('day');

    // #6280: until this session's sync has succeeded, a past day's new entries come from a schedule
    // and a disk the sync has not confirmed, so they stay in memory and the hold rebuilds those days
    // after the sync. Read once, BEFORE the disk read: a sync that succeeds while this rebuild runs
    // must not let it save past days built from what the disk held before the sync.
    const holdNewPastEntries = this.database?.initialSyncCompletedState !== true;

    const historyLogsStart_local = startDay_local.minus({ days: 14 }).startOf('day');

    // Use passed snapshot from mutex-protected caller
    const cachedEntries = cachedEntriesSnapshot;
    // Use getEntryLocalTimeInDesiredTimeZone to match how generateScheduleLogEntries buckets entries
    // This ensures entries are filtered consistently with how they'll be processed
    const cachedEntriesRangeWithHistory = cachedEntries.filter(e => {
      const entryTime_local = getEntryLocalTimeInDesiredTimeZone(e, startDay_local);
      return entryTime_local >= historyLogsStart_local && entryTime_local <= lastDay_local.endOf('day');
    });
    const storedEntriesRangeWithHistory = (await this.getLogEntriesInRangeFromDiskAsync(historyLogsStart_local, lastDay_local.endOf('day'))).filter(e => !e._deleted);
    // pass database to ensure canonical documentCache references
    // Use 'entries2' because storedEntriesRangeWithHistory is from disk and has correct _rev
    // logging is passed so the merge can report the one case no later check can see: it
    // keeps a cached body while taking the disk row's revision (#4835, see mergeLogEntries)
    const combinedEntriesRangeWithHistory = mergeLogEntries(cachedEntriesRangeWithHistory, storedEntriesRangeWithHistory, this.database, 'entries2', this.logging);

    let scheduleDays = generateScheduleDaysFromRules(this.schedule.rules, firstDay_local, lastDay_local);
    const params: PlanUpdateParams = deepClone({
      immutableState: this.immutableState,
      declaredState: this.declaredState,
      fitnessState: this.fitnessState,
      fitnessPlan: this.fitnessPlan,
      schedule: scheduleDays,
      weighIns: [],
      historyLogs: combinedEntriesRangeWithHistory,
      today_local: startDay_local.toISO(),
    });

    params.historyLogs = params.historyLogs.filter(e => e.type === LogRecordType.Workout || e.type === LogRecordType.Meal);
    params.historyLogs.map(e => {
      if (e.type === LogRecordType.Meal) {
        e.nutrients = {};
      }
    });

    const solver = new PlanUpdate();
    const result = solver.generateSchedule(params);

    const storedEntriesRange = storedEntriesRangeWithHistory.filter(e => {
      const entryTime_local = getEntryLocalTimeInDesiredTimeZone(e, startDay_local);
      return entryTime_local >= startDay_local && entryTime_local <= lastDay_local.endOf('day');
    });

    const combinedEntriesRange = combinedEntriesRangeWithHistory.filter(e => {
      const entryTime_local = getEntryLocalTimeInDesiredTimeZone(e, startDay_local);
      return entryTime_local >= startDay_local && entryTime_local <= lastDay_local.endOf('day');
    });

    // if the user has not finished the intake yet, there is no meal frequency to compute with:
    // the solver's validity check then fails on two meal counts that are NaN and says so in
    // `error`. That is the normal state of every account between sign-up and the end of the
    // intake -- it ran on every such login and was never a crash -- so it is not reported, and
    // it keeps the old behaviour below. A meal frequency that IS stored but still fails is a
    // real problem, and is both reported and refused here.
    const hasNoMealFrequencyYet = this.declaredState?.mealFrequency === undefined;
    if (result.outcome === PlanUpdateOutcome.Failed && !hasNoMealFrequencyYet) {
      this.logging.trackSilentProblem('plan-update-generate-schedule-failed', { error: result.error, where: 'generateBacklogInMemoryAsync' });
      // the solve failed, so there are no nutrient targets to rebuild the backlog from, and this
      // is the one caller whose failing path WRITES. Two shapes reach here and both are unusable:
      // a throw inside the solver hands back the schedule we passed IN, which is the same days
      // with no targets on them; a validity failure hands back a half-built schedule, which
      // `deepCheckValid` reports because it carries non-finite numbers. Rebuilding from either
      // one saves day and meal entries whose targets were never computed, and those then sync to
      // the user's other devices and stay wrong until a later solve happens to succeed.
      //
      // So return with the entries already in range: no `markForDeleteLogEntiresAsync`, no
      // rebuild, and nothing saved, which leaves the user's stored days exactly as they were. A
      // day the app has never built before then shows nothing rather than a day with blank
      // targets -- the owner chose that on 2026-09-22 (df-e08313a628f9).
      //
      // The day totals ARE still added up, because they do not come from the solver: they are the
      // sum of the meals the user actually logged, and a writer is allowed to leave a day's own
      // summary alone precisely because this runs on every backlog refill. Skipping it would show
      // a day whose total disagreed with its own meals. Nothing here is saved either way; both
      // arguments are the same array, so no meal is appended.
      //
      // This must stay ABOVE the two `markForDeleteLogEntiresAsync` calls below. They mutate the
      // canonical documentCache objects in place, so returning after one of them would leave the
      // tree dirty for whoever saves next -- which is the very write this refusal exists to stop.
      this.recomputeDayTotalsInMemory(combinedEntriesRange, combinedEntriesRange, startDay_local);
      return combinedEntriesRange;
    }
    // a failed schedule reaching here is the no-meal-frequency-yet case above, which keeps the old
    // behaviour on purpose: it builds from the schedule the solver handed back, targets or not.
    scheduleDays = result.outcome === PlanUpdateOutcome.Solved ? result.schedule : result.unsolvedSchedule;

    await this.repairHiddenDuplicateMealsAsync(storedEntriesRange, startDay_local);

    // make copies of everything before we start modifying them, to see which were changed
    const storedEntriesRangeUnmodified = storedEntriesRange.map(e => deepClone(e));
    this.markForDeleteLogEntiresAsync(storedEntriesRange);

    let scheduledLogEntries: Log_Entry[]; // all entries merged from disk and generated, updated for latest schedule, etc
    // meals the rebuild left off every slot; a new name it gives one of them is not saved (#6208)
    const mealIdsLeftOffSlots: string[] = [];

    const todayBoD = this.debug.getNowLocal().startOf('day');
    // if there is a schedule
    if (scheduleDays.length > 0) {
      this.markForDeleteLogEntiresAsync(combinedEntriesRange);

      // Diagnostic (see design-docs/sync-integrity.md): the sweep fills this with a descriptor
      // for every completed entry it rescues from deletion — the reschedule-collision actually
      // happening. Reported below, deduped per session.
      const rescuedCompletedEntries: RescuedLogEntryInfo[] = [];
      const reissuedEntryIds: ReissuedLogEntryIdInfo[] = [];
      scheduledLogEntries = generateScheduleLogEntries(scheduleDays, firstDay_local.startOf('day'), lastDay_local.endOf('day'), combinedEntriesRangeWithHistory, this.fitnessPlan, todayBoD, (error) => this.logging.trackAbnormalError(error), { rescuedCompletedEntries, reissuedEntryIds, mealIdsLeftOffSlots });
      this.reportRescuedCompletedEntries(rescuedCompletedEntries);
      this.reportReissuedEntryIds(reissuedEntryIds);
      // two different entries sharing one id would be folded into one object just below, the
      // second written over the first (#4641). The rebuild should never return that; if it does,
      // keep the one holding user data and report it, so a future handler bug cannot quietly
      // wipe a meal.
      const { entries: onePerId, dropped: droppedDuplicates } = keepOneEntryPerId(scheduledLogEntries);
      if (droppedDuplicates.length > 0) {
        this.logging.trackSilentProblem('schedule-regen-duplicate-entry-id', {
          count: droppedDuplicates.length,
          sampleId: droppedDuplicates[0].id,
          types: [...new Set(droppedDuplicates.map(d => d.type))],
          keptHoldsUserData: droppedDuplicates[0].keptHoldsUserData,
        });
      }
      scheduledLogEntries = onePerId;
      // CRITICAL: Canonicalize ALL entries to prevent race condition where concurrent calls
      // to generateBacklogInMemoryAsync create duplicate objects for the same _id.
      // If one call is saving Entry A while another creates Entry B (same _id), Entry B
      // would have no _rev and cause a conflict. By canonicalizing, we ensure all calls
      // use the same object reference, so _rev updates propagate correctly.
      scheduledLogEntries = scheduledLogEntries.map(entry => this.database.ensureCachedReference(entry));

      // if there is no schedule (this happens when called during the inital intake form)
    } else {
      // just use the eventual entries from disk (already canonical from mergeLogEntries)
      scheduledLogEntries = combinedEntriesRange;
    }

    this.recomputeDayTotalsInMemory(scheduledLogEntries, combinedEntriesRange, startDay_local);

    // #6280: record the held days. If the sync succeeded since the flag was read, the hold hears
    // that outcome at once (onInitialSyncSettled replays a settled one) and rebuilds the days.
    const backlogSaveInput = {
      storedEntriesRange, storedEntriesRangeUnmodified, scheduledLogEntries, todayBoD,
      mealIdsLeftOffSlots: new Set(mealIdsLeftOffSlots),
      holdNewPastEntries,
    };
    if (holdNewPastEntries) {
      this.pastBackfillHold?.hold(new Set(newPastEntries(backlogSaveInput)
        .map(e => getEntryLocalTimeInDesiredTimeZone(e, todayBoD).toISODate())));
    }
    const entriesToSave = chooseBacklogEntriesToSave(backlogSaveInput);

    // Defense-in-depth (see design-docs/sync-integrity.md): schedule regeneration must never
    // delete a log entry that already holds real user data or intent. The rebuild above
    // pre-marks every entry _deleted and only revives the ones it keeps, so a rebuild bug (or
    // an identity-map miss where the shared-layer revival did not reach the stored object) can
    // turn such an entry into a deletion — this is how weeks of weigh-ins were lost. The
    // decision, and why it asks what the entry HOLDS rather than what its state field says,
    // lives in user-db.delete-boundary.ts, where it can be unit tested.
    const blockedDeletes = reviveEntriesHoldingUserData(entriesToSave, scheduledLogEntries);
    // report each near-miss: the shared-layer sweep did not cover this case, so investigate
    for (const blocked of blockedDeletes) {
      this.logging.trackSilentProblem('schedule-regen-blocked-completed-delete', blocked);
    }

    // save the new and modified entries
    await this.saveEntriesAsync(DatabaseWrapper.getTransactionId(), entriesToSave);

    return scheduledLogEntries;
  }

  async convertAutomaticTrainingGoalIfNeeded() {
    // these three read an in-memory singleton rather than the database, but a
    // read can still throw — the helper releases the watcher either way.
    // Each loader registers its doc id synchronously before its first await,
    // so nothing can register this watcher again after it is released.
    const [immutableState, declaredState, fitnessState] =
      await this.database.withDocWatcherAsync(watcher => Promise.all([
        this.loadImmutableStateAsync(watcher),
        this.loadDeclaredStateAsync(watcher),
        this.loadFitnessStateAsync(watcher),
      ]));

    // convert training automatic goal if needed
    if (declaredState.trainingGoalGrowth === TrainingGoal.Automatic && fitnessState.bfData.bf_pc !== undefined) {
      declaredState.trainingGoalGrowth = isUsersTrainingGoalGrowth(
        declaredState.trainingGoalGrowth,
        immutableState.gender,
        fitnessState.bfData.bf_pc,
      );
      await this.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
    }
  }

  /* ----------------------------------------------------------------------- *
   * Day task statuses — forwarded to DayTaskStatusService
   *
   * The subsystem itself lives in `day-task-status.service.ts` (df-3b8918aefa6a).
   * The methods below stay here because pages and services already call them on
   * `UserDb`, and moving the calls would have widened a structural change into
   * every calendar screen. Nothing but forwarding happens here.
   * ----------------------------------------------------------------------- */

  /**
   * Get cached task statuses for a date range.
   * This is much faster than calculating from log entries each time.
   * Returns individual task statuses for each category.
   */
  async getCachedTaskStatusesAsync(startDate: DateTime, endDate: DateTime): Promise<Map<string, DayTaskStatuses>> {
    return this.dayTaskStatus.getCachedTaskStatusesAsync(startDate, endDate);
  }

  /** Collapse one day's per-category statuses into the single dot the calendar draws. */
  combineDayTaskStatuses(statuses: DayTaskStatuses): TaskStatus {
    return this.dayTaskStatus.combineDayTaskStatuses(statuses);
  }

  /** Run any reactive status work that is waiting on a debounce, right now. */
  flushPendingReactiveStatusUpdates(): void {
    this.dayTaskStatus.flushPendingReactiveStatusUpdates();
  }
}

